import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { GapCursor } from '@tiptap/pm/gapcursor';
import {
  BAND_COLUMN_GAP_PX,
  BAND_EDGE_INSET_PX,
  BAND_INSET_PX,
  IMAGE_HEIGHT_MAX,
  IMAGE_HEIGHT_MIN,
  MARGIN_PRESETS,
  PAGE_NUMBER_TEXT,
  SLOT_POSITIONS,
  bandsForPage,
  isAllowedImageUrl,
  normalizePageSetup,
  renderSlotText,
} from '../utils/pageSetup';

/**
 * Pagination: the Pages view (print layout) engine.
 *
 * It never changes the document. When enabled it measures the blocks of the editor and draws
 * widget decorations between blocks where a page ends, so the continuous ProseMirror element
 * looks like a stack of paper sheets. A `pageBreak` node always ends its page.
 *
 * Pages end between top-level blocks and, like Word and Google Docs, inside the wrappers that
 * hold whole stretches of a document: a generated section (`generatedField`, which wraps each
 * AI-written section of a generated document), a trust section and a blockquote are split
 * between their children (recursively), and a list (bullet, ordered, task) between its items
 * (an item stays whole, nested lists included). A wrapper is only split when its DOM holds its
 * children directly (its own DOM is its content DOM, or a node view's contentDOM reached through
 * only children with no box of their own) and is an ordinary block (see `contentHost`: normal
 * flow, visible overflow, no columns, no `break-inside: avoid`). Everything else (tables, code
 * blocks, images, diagrams, AI field cards, ...) moves whole. A widget inside a wrapper sits at
 * the position between two of its children, so it is a child of the wrapper's content element
 * (a `div` between two `li` of a list: list numbering continues, the browser keeps the list).
 * A page that ends before a wrapper's first child ends before the wrapper itself.
 *
 * Wrapped images (floats, images with data-wrap="left|right") take no room in the flow: the text
 * after them flows beside them. A float and the blocks that start beside it are kept on one page
 * (when any of them does not fit, they all move to the next page with the float), and every page
 * end is drawn below the floats on its page (the widgets have `clear: both`), so an image is
 * never cut by a page gap or drawn over the next page's text.
 *
 * Each sheet shows its header in the top margin and its footer in the bottom margin, from the
 * document's page setup (src/utils/pageSetup.js: three slots of text with live fields, an image
 * from Quill's storage, or nothing; different first page), with {page} and {pages} filled per
 * sheet. Without a page setup the footer is the centered page number (`pageNumberFormat`).
 * Orientation (landscape swaps the paper) and margins come from the page setup too.
 *
 * ## API
 *
 *   import Pagination, { computePageLayout } from '../../extensions/Pagination';
 *
 *   editor.commands.setPagination({ enabled: true, paperSize: 'letter' });  // or 'a4'
 *   editor.commands.setPagination({ enabled: false });                      // back to continuous
 *   editor.commands.setPagination({ pageNumberFormat: 'n-of-m' });           // 'n' | 'n-of-m' | 'none'
 *   editor.commands.setPagination({
 *     pageSetup,                     // page_setup (normalized here; null = defaults)
 *     fieldContext: { title, customer, project, date },   // live-field values (strings)
 *   });
 *   editor.commands.refreshPagination();   // force a full re-measure (e.g. after a CSS change)
 *   editor.storage.pagination  // { enabled, paperSize, orientation, margins, pageCount }  (pageCount is 0 when off)
 *
 *   setPagination merges with the current settings; each option is optional. `paperSize` is
 *   'letter' (default) | 'a4' | { width, height } in CSS px (portrait; the page setup's
 *   orientation turns it). `pageNumberFormat` only applies without a page setup (it picks the
 *   default footer). A change of only the fields or the bands repaints the widgets in place. All
 *   of these are decoration-only transactions (addToHistory: false), so they never touch undo
 *   history or collaboration.
 *
 *   sheetBands(setup, { firstPage, lastPage, total, fieldContext }) resolves one sheet's header
 *   and footer (pure, unit tested); effectivePageSetup(config) and sheetPaper(config) give the
 *   setup and paper the view draws.
 *
 *   computePageLayout({ blocks, pageContentHeight, chromeHeight }) is the pure layout math
 *   (documented on the function) and is what the plugin runs on its measurements.
 *
 *   A click on a spacer or the tail puts the caret on the clicked page: in text where the page
 *   has some, otherwise (a blank page between two breaks, or after a final one) a gap cursor
 *   from `emptyPageCursor(doc, pos, dir, breakNodeName)`, where typing adds a paragraph.
 *
 *   Extension options (Pagination.configure): enabled (false), paperSize ('letter'),
 *   pageNumberFormat ('n'), pageSetup (null), fieldContext (null), gap (24, used when the sheet sets no --q-page-gap),
 *   breakNodeName ('pageBreak'), transparentNodes (PAGINATION_TRANSPARENT_NODES: wrappers split
 *   between their children), listNodes (PAGINATION_LIST_NODES: lists split between items).
 *
 *   flattenPageBlocks(tree) turns the measured block tree (wrappers with children) into the flat
 *   unit list computePageLayout places; both are pure and unit tested.
 *
 * ## Geometry contract with the host (EnhancedRichTextEditor in Pages view)
 *
 *   - The ProseMirror element is the sheet column: its padding is the page margin (top, right,
 *     bottom, left are read from its computed style, so responsive margins work; the host sets it
 *     from the page setup's margins, which are also the fallback without padding). Page content
 *     height = paper height - padding-top - padding-bottom, so each sheet is exactly one paper
 *     height tall. The gap between sheets is read from the CSS variable --q-page-gap on the
 *     ProseMirror element (inherited), falling back to the `gap` option.
 *   - The element behind the sheet should be the canvas (var(--q-page-canvas)); the gap band
 *     paints that color across the full sheet width plus a few px (--q-page-bleed, default 6px)
 *     so the sheet shadow is hidden in the gap. Keep that much room around the sheet (canvas
 *     padding) so the bleed never creates a horizontal scrollbar.
 *   - The last sheet is padded to a full page by a tail widget, so the sheet always ends at a
 *     page boundary; a min-height of one paper height on the sheet covers the empty document.
 *
 * ## CSS class names and attributes (styles injected once into <head> as #quill-pagination-styles)
 *
 *   .ProseMirror.q-paginated            root class while enabled; also data-paper-size="letter|a4|custom"
 *                                       and data-orientation="portrait|landscape"
 *   .q-page-spacer                      widget before the first block of a new page (or at the end
 *                                       of the doc after a final page break); data-page="N" is the
 *                                       page it ends. Height = unused space left on page N + bottom
 *                                       margin + gap + next page's top margin.
 *     .q-page-spacer__end               the rest of page N (transparent, the sheet shows through)
 *     .q-page-spacer__gap               the canvas-colored gap between the two sheets
 *     .q-page-spacer__start             the top margin of page N + 1 (transparent)
 *     .q-page-band.q-page-footer        page N's footer, in its bottom margin (data-page = its label)
 *     .q-page-band.q-page-header        page N + 1's header, filling its top margin
 *   .q-page-tail                        widget after the last block: pads the last sheet to a full
 *     .q-page-band.q-page-footer        page and shows its footer in the sheet's bottom padding
 *                                       (a negative margin-top when a final empty paragraph
 *                                       reaches into the bottom margin, see computePageLayout)
 *   .q-page-head                        zero-height widget before the first block holding page 1's
 *     .q-page-band.q-page-header        header in the sheet's top padding (only when it shows something)
 *
 *   A band (data-band="header|footer", data-empty, data-span="center" when only the centre slot
 *   is used) holds three .q-page-band__slot (data-slot="left|center|right", data-type): text is a
 *   .q-page-band__text whose data-text attribute is shown as ::before content (never parsed as
 *   HTML, and widgets add no text to the document DOM), an image an <img> (allowlisted URL only).
 *
 *   The widgets also carry data-q-pagination="spacer|tail|head", data-q-key (decoration key),
 *   aria-hidden="true" and contenteditable="false". A spacer also has data-nested="true|false"
 *   (it sits inside a wrapper or list: its end and start bands, which always reach the sheet
 *   edges, are then painted with --q-page-sheet, so the wrapper's tint, border or review mark stops at the page
 *   end; the gap and the bands are placed from the wrapper's horizontal offset), data-forced="true|false"
 *   (it follows a manual page break) and data-label="true" when the rest of that page shows a
 *   muted "Page break" label (::before of .q-page-spacer__end). The page break node itself is
 *   collapsed to zero height in Pages view by editorContentStyles (`.q-paginated .page-break`),
 *   as in print, so it can never be drawn into the bottom margin.
 *
 *   CSS variables read (all optional): --q-page-canvas, --q-page-number (header and footer color),
 *   --q-page-gap, --q-page-bleed. Widgets are hidden when printing.
 *
 * ## Measurement (stable, cheap, no feedback loops)
 *
 *   Each block contributes its border-box height and its EFFECTIVE top/bottom margins (child
 *   margins that collapse through the block are included, see `effectiveMargin`). A floated
 *   block contributes its margin box as a float unit, a block with floats hanging out of it how far
 *   they reach (`measureFlowBlock`); where a float stacks under earlier ones is read from the
 *   layout on every measurement (`floatOffsetOf`). Adjacent
 *   margins are collapsed in the math, not read from positions, so the spacers (fixed-height
 *   blocks with no margins) can never change the next measurement. Reads are batched in one
 *   requestAnimationFrame after doc changes; results are cached per block DOM element and PM
 *   node (unchanged blocks are not re-read). A wrapper is never measured as a whole (its height
 *   includes the spacers inside it): only its own margins and insets and its children, so the
 *   widgets can never feed back into the measurement through margins or wrapper heights. Two
 *   places where a widget does change what is measured are held steady: a block pushed below a
 *   float that starts its sheet (the spacer clears the float, so the block is wider and shorter
 *   there) keeps the height it had beside the float while the page still ends before it
 *   (`besideHeights`), and a float with a widget in front of it is read with that widget
 *   hidden, so it is placed where it sits without one (see floatOffsetOf). A ResizeObserver on the sheet (width
 *   changes) and on every measured block (node views, Mermaid, images, previews growing), image
 *   `load` events and `document.fonts` re-measure only what changed. Identical results do nothing;
 *   height-only changes repaint the existing widget DOM in place (ProseMirror ignores mutations
 *   inside widgets) and only a change in where pages end dispatches a decoration transaction.
 *   Nothing runs while an IME composition is active.
 *   Where pages end comes from the math alone; the widget HEIGHTS are then snapped to the real
 *   layout (`alignToSheets`: where each widget sits relative to the one before, which no widget
 *   height changes), so sub-pixel margins never make sheets drift over a long document. Freshly
 *   drawn widgets are aligned by one follow-up frame that repaints in place.
 *
 * Widgets are contenteditable=false, aria-hidden, non-selectable, keyed by page number (so
 * ProseMirror reuses their DOM), ignore selection and stop all events except drag and drop.
 * The controller finds the live widget elements in the DOM on every measurement (by data-q-key),
 * so in-place repaints keep working after a plugin reconfigure recreates the plugin view.
 * Clicking the end of a page puts the caret at the end of that page's text; clicking the start
 * of the next page (or the gap) puts it at the start of the next page's text (a text caret is
 * preferred, so a page break or image next to the widget is never node-selected). The bands
 * reach the sheet edges, so the same holds for a click in a side margin beside a page end.
 */

export const paginationPluginKey = new PluginKey('quillPagination');

/** Paper sizes in CSS px at 96 px/in (match src/utils/paperSize.js). */
export const PAGINATION_PAPER_SIZES = Object.freeze({
  letter: Object.freeze({ id: 'letter', width: 816, height: 1056 }),
  a4: Object.freeze({ id: 'a4', width: 794, height: 1123 }),
});

/** Class names used by the widgets (see the header comment). */
export const PAGINATION_CLASSES = Object.freeze({
  root: 'q-paginated',
  spacer: 'q-page-spacer',
  end: 'q-page-spacer__end',
  gap: 'q-page-spacer__gap',
  start: 'q-page-spacer__start',
  tail: 'q-page-tail',
  head: 'q-page-head',
  band: 'q-page-band',
  header: 'q-page-header',
  footer: 'q-page-footer',
  slot: 'q-page-band__slot',
  text: 'q-page-band__text',
});

const STYLE_ID = 'quill-pagination-styles';
const PAGE_NUMBER_FORMATS = ['n', 'n-of-m', 'none'];
/** Sub-pixel tolerance so fractional measurements never push an exactly fitting block. */
const FIT_EPSILON = 0.5;
/** Smallest page content height the engine accepts (guards absurd paddings). */
const MIN_CONTENT_HEIGHT = 120;
/** Default page margin used when the sheet has no padding (px, 1 in). */
const DEFAULT_MARGIN = 96;
/** Max depth when following margins that collapse through nested blocks. */
const MARGIN_DEPTH = 4;
/** Room the rest of a page needs to show the "Page break" label after a manual break (px). */
const MIN_BREAK_LABEL_ROOM = 32;
/** Retry delay while an IME composition is active (ms). */
const COMPOSITION_RETRY_MS = 150;
/** Max wrapper nesting the engine descends into (deeper wrappers are measured whole). */
const MAX_NEST_DEPTH = 8;
/**
 * Wrapper blocks whose children the page may end between (when their DOM allows it, see
 * `contentHost`): generated-document sections, trust sections and blockquotes.
 */
export const PAGINATION_TRANSPARENT_NODES = Object.freeze(['generatedField', 'trustSection', 'blockquote']);
/** Lists: the page may end between their items (an item, nested lists included, stays whole). */
export const PAGINATION_LIST_NODES = Object.freeze(['bulletList', 'orderedList', 'taskList']);

/**
 * A finite, non-negative number (anything else becomes 0).
 * @param {*} value - Value.
 * @returns {number} Number.
 */
const size = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * A finite number (anything else becomes 0); margins may be negative.
 * @param {*} value - Value.
 * @returns {number} Number.
 */
const finite = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * CSS adjoining-margin collapsing of two margins.
 * @param {number} a - First margin (px).
 * @param {number} b - Second margin (px).
 * @returns {number} Collapsed margin (px).
 */
export function collapseMargins(a, b) {
  const x = finite(a);
  const y = finite(b);
  if (x >= 0 && y >= 0) return Math.max(x, y);
  if (x < 0 && y < 0) return Math.min(x, y);
  return x + y;
}

/**
 * Resolve a paper size option.
 * @param {'letter'|'a4'|{width: number, height: number}|undefined} paperSize - Option.
 * @returns {{id: string, width: number, height: number}} Paper (px).
 */
export function resolvePaper(paperSize) {
  if (paperSize && typeof paperSize === 'object' && size(paperSize.width) && size(paperSize.height)) {
    return { id: paperSize.id || 'custom', width: size(paperSize.width), height: size(paperSize.height) };
  }
  const id = String(paperSize || '').toLowerCase();
  return PAGINATION_PAPER_SIZES[id] || PAGINATION_PAPER_SIZES.letter;
}

/**
 * Pure page layout.
 *
 * Coordinates are page content coordinates (0 = top of the content area, below the top margin).
 * The rules follow how the browser prints the same HTML (the PDF export) and Word's
 * keep-with-next, so page ends match the exports:
 *   - A block fits when its border-box bottom is within the page content height. Blocks are
 *     never split: one that does not fit moves whole to the next page (the exports keep blocks
 *     together the same way, see enhancedExportService).
 *   - Blocks here are the units the page may end between: `flattenPageBlocks` turns transparent
 *     wrappers (generated sections, trust sections, blockquotes) and lists into their children
 *     first, so a long section or list is split between its paragraphs or items.
 *   - A heading (`keepWithNext`, several in a row count as one run) never ends a page: when the
 *     block after it does not fit, the heading moves to the next page with it, provided the
 *     heading is not the first block of its page and the two fit on a page together. When they
 *     do not, the block flows on its own as it would without the rule (no endless push), except
 *     a block taller than a page: that one moves with the heading and starts right below it,
 *     also when the heading starts the sheet, stretching that sheet (next rule). Headings that
 *     start a sheet never move (that would leave a blank page), so when the block after them
 *     fits a page but not below them, they stay alone on their page, as in Word with
 *     keep-with-next and keep-lines-together (blocks are never split by line).
 *   - At the top of a page after an automatic (unforced) page end, the block's top margin is
 *     dropped, as print does. The spacer before it is `shift` px shorter so the margin the block
 *     still has on screen lands exactly at the next page's content top. The first block of the
 *     document and the first block after a page break keep their top margin.
 *   - A page break block is always kept on its page (never pushed) and ends it (`forced`).
 *   - A block taller than a page starts on a fresh page (unless it already starts one, or follows
 *     the headings it is kept with) and
 *     stretches that sheet to as many whole pages as it covers, gaps included (no gaps are drawn
 *     inside it); its page number label shows the range. Following blocks fill the rest of the
 *     stretched sheet. One pass, bounded backtracking for headings, never loops.
 *   - The last block, when it is an empty paragraph (`trailingEmpty`: the paragraph the editor
 *     keeps after a generated section or table so there is somewhere to type), never opens a page
 *     of its own: when it does not fit it stays on the last page, reaching at most
 *     `trailingAllowance` px into the bottom margin (it shows nothing there but the caret). The
 *     last page's `lastPageFill` is then negative (the tail pulls up by that much, so the sheet
 *     still ends at a page boundary). Once it holds text it moves to a new page as usual.
 *   - Wrapped images (floats, `floating`: `height` is the margin box, the margins are ignored)
 *     take no room in the flow: a float's top is the previous block's bottom margin edge (plus
 *     `floatOffset` when it is stacked under earlier floats; a float that starts a sheet sits at
 *     its top) and the blocks after it flow beside it. A float and the blocks that start beside
 *     it are one group the page never ends inside: when any of them does not fit, the group
 *     moves whole to the next page (headings before it are kept with it), unless it already
 *     starts the sheet. A page end is drawn below every float on the sheet (the widgets clear
 *     floats), so the unused space is measured from the lowest float too. `floatBottom` on an
 *     in-flow block is how far floats inside it reach below its top; `avoidsFloats` (CSS clear)
 *     starts a block below the floats.
 *
 * @param {object} input - Input.
 * @param {Array<{height: number, marginTop?: number, marginBottom?: number, isBreak?: boolean, keepWithNext?: boolean, trailingEmpty?: boolean, floating?: boolean, floatOffset?: number, floatBottom?: number, avoidsFloats?: boolean}>} input.blocks
 *   Top-level blocks in order: border-box height and effective margins (px).
 * @param {number} input.pageContentHeight - Content height of one page (px).
 * @param {number} [input.chromeHeight=0] - Space a spacer adds besides the unused fill: bottom
 *   margin + gap + next page's top margin (px).
 * @param {number} [input.pageMarginTop] - The page's top margin (px): the most a dropped block
 *   margin can shorten a spacer by (defaults to chromeHeight).
 * @param {number} [input.trailingAllowance=0] - How far a final empty paragraph may reach into
 *   the bottom margin instead of opening a page of its own (px).
 * @returns {{
 *   spacers: Array<{beforeIndex: number, fill: number, height: number, pageNumberEnded: number,
 *     pageNumberStarted: number, forced: boolean, shift: number, besideFloat?: true}>,
 *   pageCount: number,
 *   lastPageFill: number,
 *   lastSheetFirstPage: number,
 *   blockPages: number[],
 * }} `beforeIndex` is the block the spacer precedes (blocks.length = after the last block);
 *   `fill` is the unused content space left on the ended page below the previous block's bottom
 *   margin (may be slightly negative when only that margin overflows); `height` = max(0, fill +
 *   chromeHeight - shift); `pageNumberStarted` is the first page of the ended sheet (less than
 *   `pageNumberEnded` for a stretched sheet); `forced` is true after a page break; `shift` is the
 *   dropped top margin of the next block; `lastPageFill` is the unused space on the last page
 *   (for the tail pad); `blockPages[i]` is the page block i starts on. `besideFloat` marks a page
 *   end before a block that started beside a float group starting that sheet (the group cannot
 *   move: the block goes below the floats on the next page).
 */
export function computePageLayout({ blocks, pageContentHeight, chromeHeight = 0, pageMarginTop, trailingAllowance = 0 } = {}) {
  const list = Array.isArray(blocks) ? blocks : [];
  const pageHeight = Number(pageContentHeight);
  if (!Number.isFinite(pageHeight) || pageHeight <= 0) {
    return { spacers: [], pageCount: 1, lastPageFill: 0, lastSheetFirstPage: 1, blockPages: list.map(() => 1) };
  }
  const chrome = size(chromeHeight);
  const allowance = size(trailingAllowance);
  const shiftLimit = pageMarginTop === undefined || pageMarginTop === null ? chrome : Math.min(size(pageMarginTop), chrome);
  const stride = pageHeight + chrome; // one page plus the chrome between two pages
  const spacers = [];
  const blockPages = [];
  const isFloat = (block) => Boolean(block && block.floating);

  let firstPage = 1; // number of the first page the current sheet covers
  let sheetPages = 1; // pages the current sheet covers (more than 1 when stretched by a tall block)
  let capacity = pageHeight; // content height of the current sheet
  let y = 0; // border-box bottom of the last in-flow block on the sheet
  let prevMarginBottom = 0;
  let placed = 0; // blocks (floats included) on the current sheet
  let keepTopMargin = true; // first sheet, or the sheet after a page break
  let topShift = 0; // top margin dropped from the first block of the current sheet
  let floatLimit = 0; // lowest float bottom (margin box) on the sheet
  let groupStart = -1; // first float of the floats the text still flows beside (-1: none)
  let runBase = 0; // offset of the first float of a run that starts a sheet (see floatTop)
  let history = []; // placement state before each block on the current sheet (backtracking)

  const lastPageOfSheet = () => firstPage + sheetPages - 1;
  // Unused space: below the last block's bottom margin and below every float (the widgets clear
  // floats, so the page end is drawn under a wrapped image, never across it).
  const unused = () => capacity - Math.max(y + (placed ? Math.max(0, prevMarginBottom) : 0), floatLimit);
  // Top of a block that starts a fresh sheet after an automatic page end (its top margin is
  // dropped by up to the page's top margin, see endSheet).
  const freshTop = (marginTop) => Math.max(0, Math.max(0, marginTop) - shiftLimit);
  const endSheet = (beforeIndex, forced, besideFloat = false) => {
    const next = list[beforeIndex];
    // A float is placed at the spacer's bottom whatever the margins of the text after it, so a
    // page that starts with a float drops no margin (the text keeps its own).
    const shift = forced || !next || isFloat(next) ? 0 : Math.min(Math.max(0, finite(next.marginTop)), shiftLimit);
    const fill = unused();
    const ended = lastPageOfSheet();
    spacers.push({
      beforeIndex,
      fill,
      height: Math.max(0, fill + chrome - shift),
      pageNumberEnded: ended,
      pageNumberStarted: firstPage,
      forced,
      shift,
      // The block after it started beside a float group that starts the sheet (see below).
      ...(besideFloat ? { besideFloat: true } : {}),
    });
    firstPage = ended + 1;
    sheetPages = 1;
    capacity = pageHeight;
    y = 0;
    prevMarginBottom = 0;
    placed = 0;
    keepTopMargin = forced;
    topShift = shift;
    floatLimit = 0;
    groupStart = -1;
    runBase = 0;
    history = [];
  };
  const sheetTop = (marginTop) => (keepTopMargin ? Math.max(0, marginTop) : Math.max(0, Math.max(0, marginTop) - topShift));
  // A float's margin box starts right below the previous block's bottom margin (the margins of
  // the text after it do not move it), plus `floatOffset` when it is stacked under earlier
  // floats. On a sheet that starts with floats, offsets count from the first of them. A float
  // that leads a wrapper sits at the wrapper's content top: `floatLead` is the wrapper's top
  // margin, which collapses with the previous block's bottom margin; a float that trails a
  // wrapper sits `floatBack` above the wrapper's bottom edge (its inset and margin).
  const floatTop = (block) => {
    const offset = size(block.floatOffset);
    if (!placed) return 0;
    const lead = block.floatLead === undefined || block.floatLead === null
      ? Math.max(0, prevMarginBottom)
      : Math.max(0, collapseMargins(prevMarginBottom, block.floatLead));
    return Math.max(0, y + lead + Math.max(0, offset - runBase) - size(block.floatBack));
  };
  const topOf = (block) => {
    if (isFloat(block)) return floatTop(block);
    const marginTop = finite(block && block.marginTop);
    let top = placed ? y + collapseMargins(prevMarginBottom, marginTop) : sheetTop(marginTop);
    // A block that clears floats (`clear`) starts below them.
    if (block && block.avoidsFloats && top < floatLimit) top = floatLimit;
    return top;
  };
  /** How far down a block reaches from its top: its box, or the floats inside it. */
  const reachOf = (block) => Math.max(size(block && block.height), isFloat(block) ? 0 : size(block && block.floatBottom));
  const restore = (entry) => {
    y = entry.yBefore;
    prevMarginBottom = entry.marginBottomBefore;
    placed = entry.placedBefore;
    floatLimit = entry.floatLimitBefore;
    groupStart = entry.groupStartBefore;
    runBase = entry.runBaseBefore;
    blockPages.length = entry.index;
  };

  let index = 0;
  while (index < list.length) {
    const block = list[index];
    const floating = isFloat(block);
    const height = size(block && block.height);
    const marginTop = floating ? 0 : finite(block && block.marginTop);
    const marginBottom = floating ? 0 : finite(block && block.marginBottom);
    const isBreak = Boolean(block && block.isBreak) && !floating;

    let top = topOf(block);
    let reach = reachOf(block);
    let flowsBelowHeadings = false;
    // Starts beside the floats on the sheet (before any group move below changes the state).
    const besideFloats = !floating && groupStart >= 0 && top < floatLimit - FIT_EPSILON;
    // A final empty paragraph that only just misses the page stays on it (no blank last page).
    const keepTrailing = Boolean(block && block.trailingEmpty) && index === list.length - 1 && placed > 0
      && !isBreak && !floating && top + reach - capacity <= allowance + FIT_EPSILON;
    if (!isBreak && !keepTrailing && placed && top + reach > capacity + FIT_EPSILON) {
      // Wrapped images: a float and the text flowing beside it are never separated by a page
      // end; when any of them does not fit, the whole group moves to the next page (unless the
      // group already starts this sheet: then the page ends below the floats).
      let at = index;
      let atTop = top;
      let atMarginTop = marginTop;
      if (groupStart >= 0 && top < floatLimit - FIT_EPSILON) {
        const startIndex = groupStart;
        const g = history.find((entry) => entry.index === startIndex);
        if (g && g.placedBefore > 0) {
          at = g.index;
          atTop = g.top;
          atMarginTop = g.marginTop;
          history = history.slice(0, history.indexOf(g));
          restore(g);
        }
      }
      const extentBottom = top + reach; // where the block (and any group it is in) ends
      // Keep with next: the headings right before this block never end the page. They go to
      // the next page with it when they fit there together, or when the block is taller than a
      // page anyway (it then starts right below them). When they would not fit together on any
      // page, the block flows on its own (no endless push). Headings that already start the
      // sheet stay; a block taller than a page then follows them on the same (stretched) sheet.
      let first = history.length;
      while (first > 0 && history[first - 1].keepWithNext) first -= 1;
      const head = first < history.length ? history[first] : null;
      const tallAlone = freshTop(atMarginTop) + (extentBottom - atTop) > pageHeight + FIT_EPSILON;
      if (head && head.placedBefore > 0
        && (tallAlone || freshTop(head.marginTop) + (extentBottom - head.top) <= pageHeight + FIT_EPSILON)) {
        restore(head);
        endSheet(head.index, false);
        index = head.index;
        continue;
      }
      flowsBelowHeadings = at === index && Boolean(head && head.placedBefore === 0 && tallAlone);
      if (!flowsBelowHeadings) {
        if (at !== index) {
          // The group moves: lay it out again from its first float on the next sheet.
          endSheet(at, false);
          index = at;
          continue;
        }
        // Beside a float group that starts this sheet: the page ends right before the block,
        // which then lays out below the floats (the spacer clears them) and may change height
        // there; the controller holds its height (see PaginationController besideHeights).
        endSheet(index, false, besideFloats);
        top = topOf(block);
        reach = reachOf(block);
      }
    }
    const bottom = top + reach;
    if ((!placed || flowsBelowHeadings) && bottom > capacity + FIT_EPSILON) {
      // Taller than a page (at the top of a sheet, or right below the headings it is kept
      // with): let it overflow, as whole pages (with gaps).
      sheetPages = Math.max(sheetPages, Math.ceil((bottom - FIT_EPSILON + chrome) / stride));
      capacity = sheetPages * pageHeight + (sheetPages - 1) * chrome;
    }
    history.push({
      index,
      top,
      marginTop,
      yBefore: y,
      marginBottomBefore: prevMarginBottom,
      placedBefore: placed,
      floatLimitBefore: floatLimit,
      groupStartBefore: groupStart,
      runBaseBefore: runBase,
      keepWithNext: Boolean(block && block.keepWithNext) && !isBreak && !floating,
    });
    blockPages.push(firstPage + Math.min(Math.floor(Math.max(0, top) / stride), sheetPages - 1));
    if (floating) {
      if (!placed) runBase = size(block.floatOffset);
      // A float below every earlier one starts a new group of floats and the text beside them.
      if (groupStart < 0 || top >= floatLimit - FIT_EPSILON) groupStart = index;
      floatLimit = Math.max(floatLimit, top + height);
    } else {
      runBase = 0;
      const floatsInside = size(block && block.floatBottom) > height + FIT_EPSILON;
      // Text that starts below every float no longer flows beside one.
      if (top >= floatLimit - FIT_EPSILON) groupStart = floatsInside ? index : -1;
      if (floatsInside) floatLimit = Math.max(floatLimit, top + size(block.floatBottom));
      y = top + height;
      prevMarginBottom = marginBottom;
    }
    placed += 1;
    if (isBreak) endSheet(index + 1, true);
    index += 1;
  }

  return { spacers, pageCount: lastPageOfSheet(), lastPageFill: unused(), lastSheetFirstPage: firstPage, blockPages };
}

/**
 * Flatten a measured block tree into the units `computePageLayout` places (pure).
 *
 * A wrapper (a node with `children`) is replaced by its children, recursively, with its own box
 * folded into the first and last of them the way CSS lays it out:
 *   - its top margin collapses with the first child's when nothing separates them
 *     (`collapseTop`, default: no `insetTop`); otherwise the inset (padding + border) and the
 *     child's margin become part of the first child's height and the wrapper's margin is the
 *     first child's outer margin. The same at the bottom with the last child. Floats
 *     (`floating`) take no part in margins: the first and last in-flow children do, and floats
 *     before the first in-flow child are offset by the wrapper's top margin and inset.
 *   - the first child takes the wrapper's `at`: a page that ends right before a wrapper ends
 *     before the wrapper itself (outside it), so the spacer never sits inside the wrapper as its
 *     first child. Every other child keeps its own `at` (a position between two children).
 *   - `extraBefore` (in-flow elements other plugins put before the node) adds to the height of
 *     its first in-flow unit (and to the offset of floats before it).
 * A wrapper with no children is a plain block of its own `height`.
 *
 * @param {Array<object>} tree - Nodes: leaves `{height, marginTop, marginBottom, isBreak,
 *   keepWithNext, trailingEmpty, floating, floatOffset, floatBottom, avoidsFloats, at, extraBefore}`
 *   or wrappers `{children, marginTop, marginBottom, insetTop, insetBottom, collapseTop,
 *   collapseBottom, at, extraBefore}` (px; `at` is opaque).
 * @returns {Array<{height: number, marginTop: number, marginBottom: number, isBreak: boolean,
 *   keepWithNext: boolean, trailingEmpty: boolean, floating: boolean, floatOffset: number,
 *   floatBottom: number, avoidsFloats: boolean, at: *, floatLead?: number, floatBack?: number, el?: Element}>}
 *   Units in document order, ready for computePageLayout (`floatLead` / `floatBack`: floats that
 *   lead or trail a wrapper, see computePageLayout floatTop; `el`: the leaf's measured element).
 */
export function flattenPageBlocks(tree) {
  const out = [];
  const leadingFloats = (units, add) => {
    if (!add) return;
    for (let i = 0; i < units.length && units[i].floating; i += 1) units[i].floatOffset += add;
  };
  const hasLead = (unit) => unit.floatLead !== undefined && unit.floatLead !== null;
  const flatten = (node, depth) => {
    const units = [];
    const kids = node && Array.isArray(node.children) ? node.children : null;
    if (!kids || !kids.length || depth > MAX_NEST_DEPTH) {
      const floating = Boolean(node && node.floating);
      units.push({
        height: size(node && node.height),
        marginTop: floating ? 0 : finite(node && node.marginTop),
        marginBottom: floating ? 0 : finite(node && node.marginBottom),
        isBreak: !floating && Boolean(node && node.isBreak),
        keepWithNext: !floating && Boolean(node && node.keepWithNext),
        trailingEmpty: !floating && Boolean(node && node.trailingEmpty),
        floating,
        floatOffset: floating ? size(node && node.floatOffset) : 0,
        floatBottom: floating ? 0 : size(node && node.floatBottom),
        avoidsFloats: Boolean(node && node.avoidsFloats),
        at: node ? node.at : undefined,
      });
      // The measured element (the controller's per-element memory; not used by the layout).
      if (node && node.el) units[units.length - 1].el = node.el;
    } else {
      kids.forEach((kid) => {
        flatten(kid, depth + 1).forEach((unit) => units.push(unit));
      });
      const inFlow = units.filter((unit) => !unit.floating);
      const first = inFlow[0] || null;
      const last = inFlow[inFlow.length - 1] || null;
      const insetTop = size(node.insetTop);
      const insetBottom = size(node.insetBottom);
      const collapseTop = node.collapseTop === undefined ? insetTop === 0 : Boolean(node.collapseTop);
      const collapseBottom = node.collapseBottom === undefined ? insetBottom === 0 : Boolean(node.collapseBottom);
      if (first && collapseTop) {
        first.marginTop = collapseMargins(node.marginTop, first.marginTop);
      } else if (first) {
        first.height = size(first.height + insetTop + first.marginTop);
        first.marginTop = finite(node.marginTop);
      }
      // Floats before the first in-flow child sit at the wrapper's content top (floatOffsetOf
      // measures from there): below its top inset, and below its top margin, which collapses
      // with the previous block's bottom margin (and, with nothing between them, with the first
      // child's margin), so the margin is carried as `floatLead`, never added to the offset.
      for (let i = 0; i < units.length && units[i].floating; i += 1) {
        const unit = units[i];
        const inner = hasLead(unit) ? unit.floatLead : 0;
        if (collapseTop) {
          unit.floatLead = collapseMargins(first ? first.marginTop : finite(node.marginTop), inner);
          unit.floatOffset += insetTop;
        } else {
          // The inset separates the margins: the inner ones lie inside, below the content top.
          unit.floatOffset += insetTop + Math.max(0, inner);
          unit.floatLead = finite(node.marginTop);
        }
      }
      if (last) {
        // Floats after the last in-flow child hang from that child's own bottom margin edge,
        // above the wrapper's bottom inset and margin that the fold below puts after it.
        const ownMarginBottom = last.marginBottom;
        const back = collapseBottom
          ? Math.max(0, collapseMargins(ownMarginBottom, node.marginBottom)) - Math.max(0, ownMarginBottom)
          : insetBottom + Math.max(0, finite(node.marginBottom));
        if (back) {
          for (let i = units.lastIndexOf(last) + 1; i < units.length; i += 1) {
            if (units[i].floating) units[i].floatBack = size(units[i].floatBack) + back;
          }
        }
      }
      if (last && collapseBottom) {
        last.marginBottom = collapseMargins(last.marginBottom, node.marginBottom);
      } else if (last) {
        last.height = size(last.height + last.marginBottom + insetBottom);
        last.marginBottom = finite(node.marginBottom);
      }
      units[0].at = node.at;
    }
    const extra = size(node && node.extraBefore);
    if (extra) {
      leadingFloats(units, extra);
      const target = units.find((unit) => !unit.floating);
      if (target) target.height += extra;
    }
    return units;
  };
  (Array.isArray(tree) ? tree : []).forEach((node) => {
    flatten(node, 0).forEach((unit) => out.push(unit));
  });
  return out;
}

/* ------------------------------------------------------------------------------------------ */
/* DOM measurement                                                                              */
/* ------------------------------------------------------------------------------------------ */

/**
 * Computed style of an element (null outside a browser).
 * @param {Element} el - Element.
 * @returns {CSSStyleDeclaration|null} Style.
 */
const styleOf = (el) => {
  try {
    const win = el && el.ownerDocument && el.ownerDocument.defaultView;
    return win && typeof win.getComputedStyle === 'function' ? win.getComputedStyle(el) : null;
  } catch {
    return null;
  }
};

const px = (value) => finite(parseFloat(value));

/**
 * Whether an element is taken out of normal flow (or not rendered).
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @returns {boolean} True when it takes no space in the flow.
 */
const outOfFlow = (style) => Boolean(style) && (
  style.display === 'none'
  || style.position === 'absolute'
  || style.position === 'fixed'
  || (style.float && style.float !== 'none')
);

/**
 * Whether a child's margin on `side` collapses through this element (CSS 2.1 8.3.1): a plain
 * block box with no padding or border on that side and visible overflow.
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @param {'top'|'bottom'} side - Side.
 * @returns {boolean} True when it collapses through.
 */
const collapsesThrough = (style, side) => {
  if (!style) return false;
  if (style.display !== 'block' && style.display !== 'list-item') return false;
  const visible = (v) => !v || v === 'visible' || v === 'clip';
  if (!visible(style.overflow) || !visible(style.overflowX) || !visible(style.overflowY)) return false;
  const Side = side === 'top' ? 'Top' : 'Bottom';
  if (px(style[`padding${Side}`]) !== 0) return false;
  if (px(style[`border${Side}Width`]) !== 0 && style[`border${Side}Style`] && style[`border${Side}Style`] !== 'none') return false;
  return true;
};

/**
 * First (or last) in-flow child element whose margin can adjoin the parent's, or null when
 * text or inline content sits there (inline boxes never collapse with the parent).
 * @param {Element} el - Parent.
 * @param {'top'|'bottom'} side - Side.
 * @returns {Element|null} Child.
 */
const edgeChild = (el, side) => {
  const nodes = Array.from(el.childNodes || []);
  if (side === 'bottom') nodes.reverse();
  for (const node of nodes) {
    if (node.nodeType === 3) {
      if (/\S/.test(node.nodeValue || '')) return null;
      continue;
    }
    if (node.nodeType !== 1) continue;
    const style = styleOf(node);
    if (outOfFlow(style)) continue;
    if (!style || (style.display !== 'block' && style.display !== 'list-item' && style.display !== 'flow-root'
      && style.display !== 'flex' && style.display !== 'grid' && style.display !== 'table')) return null;
    return node;
  }
  return null;
};

/**
 * Effective outer margin of a block on one side: its own margin collapsed with the margins of
 * nested first/last children that collapse through it.
 * @param {Element} el - Block element.
 * @param {'top'|'bottom'} side - Side.
 * @param {number} [depth=0] - Recursion depth.
 * @returns {number} Margin (px).
 */
export function effectiveMargin(el, side, depth = 0) {
  const style = styleOf(el);
  if (!style) return 0;
  const own = px(side === 'top' ? style.marginTop : style.marginBottom);
  if (depth >= MARGIN_DEPTH || !collapsesThrough(style, side)) return own;
  const child = edgeChild(el, side);
  return child ? collapseMargins(own, effectiveMargin(child, side, depth + 1)) : own;
}

/**
 * Measure one top-level block.
 * @param {Node|null} dom - Block DOM.
 * @returns {{height: number, marginTop: number, marginBottom: number}} Box.
 */
export function measureBlock(dom) {
  if (!dom || dom.nodeType !== 1 || typeof dom.getBoundingClientRect !== 'function') {
    return { height: 0, marginTop: 0, marginBottom: 0 };
  }
  const style = styleOf(dom);
  if (outOfFlow(style)) return { height: 0, marginTop: 0, marginBottom: 0 };
  const rect = dom.getBoundingClientRect();
  return {
    height: size(rect && rect.height),
    marginTop: effectiveMargin(dom, 'top'),
    marginBottom: effectiveMargin(dom, 'bottom'),
  };
}

/**
 * Whether an element is a float (a wrapped image's node view, a plain floated <img>, ...).
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @returns {boolean} True when it floats.
 */
const isFloatStyle = (style) => Boolean(style) && style.display !== 'none' && Boolean(style.float) && style.float !== 'none';

/** Elements that may float inside a block (wrapped images). */
const FLOAT_CANDIDATES = '[data-wrap="left"],[data-wrap="right"],img';

/**
 * Measure a block for the layout with its floats: a floated block is a float unit (its margin
 * box height; floats keep their margins), any other block its box (measureBlock) plus how far
 * floats inside it reach below its top when they hang out of it (`floatBottom`), and whether it
 * clears floats (`avoidsFloats`).
 * @param {Node|null} dom - Block DOM.
 * @returns {{height: number, marginTop: number, marginBottom: number, floating?: boolean,
 *   floatBottom?: number, avoidsFloats?: boolean}} Box.
 */
export function measureFlowBlock(dom) {
  if (!dom || dom.nodeType !== 1 || typeof dom.getBoundingClientRect !== 'function') return measureBlock(dom);
  const style = styleOf(dom);
  if (isFloatStyle(style)) {
    const rect = dom.getBoundingClientRect();
    return {
      height: size(rect && rect.height) + Math.max(0, px(style.marginTop)) + Math.max(0, px(style.marginBottom)),
      marginTop: 0,
      marginBottom: 0,
      floating: true,
    };
  }
  const box = measureBlock(dom);
  if (style && style.clear && style.clear !== 'none') box.avoidsFloats = true;
  if (typeof dom.querySelectorAll === 'function') {
    let reach = 0;
    let top = null;
    dom.querySelectorAll(FLOAT_CANDIDATES).forEach((candidate) => {
      // The outermost floated element between the candidate and the block.
      let floated = null;
      for (let el = candidate; el && el !== dom; el = el.parentElement) {
        if (isFloatStyle(styleOf(el))) floated = el;
      }
      if (!floated) return;
      if (top === null) top = dom.getBoundingClientRect().top;
      const rect = floated.getBoundingClientRect();
      reach = Math.max(reach, rect.bottom + Math.max(0, px(styleOf(floated).marginBottom)) - top);
    });
    if (reach > box.height + FIT_EPSILON) box.floatBottom = reach;
  }
  return box;
}

/** Siblings read past a float's anchor when looking for the float it is stacked under. */
const FLOAT_LOOKBACK = 12;

/**
 * Bottom of an element's margin box (px, viewport coordinates).
 * @param {Element} el - Element.
 * @returns {number} Bottom.
 */
const marginBottomEdge = (el) => el.getBoundingClientRect().bottom + Math.max(0, px(styleOf(el) && styleOf(el).marginBottom));

/**
 * Where a float sits below the point the layout places it at (the previous in-flow sibling's
 * bottom margin edge, or the content top of its container): more than 0 when it is stacked
 * under earlier floats. Read from the current layout.
 *
 * When pagination widgets sit between the float and that point, the float is drawn past a page
 * end (the widgets clear floats), so its own position says nothing about where it would sit
 * without them, and floats after the widget that sat beside each other (a left and a right
 * float) would read as stacked. Those widgets are hidden for the read (display: none, restored
 * right after; ProseMirror ignores mutations inside widgets), so the offset is the one the float
 * has with no page end in front of it, whatever an earlier layout put there. The page end then
 * follows the real float group, and a layout reached through other setups (margins, paper)
 * matches a fresh one. Only when that read is impossible (no style to set) does `memo` (what an
 * earlier read saw: under which earlier float it was stacked, or none) or, last, `stacked` (the
 * floats right before it count as stacked) place it. Every read refreshes the memo.
 * @param {HTMLElement} el - Floated element.
 * @param {HTMLElement} container - Its container.
 * @param {number} stacked - Margin box heights of the floats right before it (the last fallback).
 * @param {WeakMap<Element, {under: Element|null}>} [memos] - Per-float memo.
 * @returns {number} Offset (px).
 */
export const floatOffsetOf = (el, container, stacked, memos) => {
  let anchorEl = null;
  const widgets = [];
  const floatsBefore = [];
  let afterAnchor = 0;
  for (let prev = el.previousElementSibling; prev; prev = prev.previousElementSibling) {
    // Past the anchor only a few siblings are read (earlier floats the float may stack under).
    if (anchorEl !== null && (afterAnchor += 1) > FLOAT_LOOKBACK) break;
    const kind = prev.getAttribute(WIDGET_ATTR);
    if (kind === 'head') continue; // zero height, at the very top
    if (kind) {
      // Only a widget between the float and its anchor matters.
      if (anchorEl === null) widgets.push(prev);
      continue;
    }
    const prevStyle = styleOf(prev);
    if (isFloatStyle(prevStyle)) {
      floatsBefore.push(prev);
      continue;
    }
    if (outOfFlow(prevStyle)) continue;
    // Earlier floats before the anchor can still reach below it (a float with text beside it).
    if (anchorEl === null) anchorEl = prev;
  }
  const clamp = (value) => (Number.isFinite(value) && value > FIT_EPSILON ? value : 0);
  const read = () => {
    let anchor;
    if (anchorEl) {
      anchor = anchorEl.getBoundingClientRect().bottom + Math.max(0, effectiveMargin(anchorEl, 'bottom'));
    } else {
      const cs = styleOf(container);
      anchor = container.getBoundingClientRect().top + finite(container.clientTop) + px(cs && cs.paddingTop);
    }
    const style = styleOf(el);
    const top = el.getBoundingClientRect().top - Math.max(0, px(style && style.marginTop));
    const offset = clamp(top - anchor);
    // The earlier float it sits right under (none when it sits at its anchor).
    const under = offset > 0 ? floatsBefore.find((prev) => Math.abs(marginBottomEdge(prev) - top) <= 1) || null : null;
    return { offset, under, anchor };
  };
  const remember = ({ offset, under }) => {
    if (!memos) return;
    if (offset === 0 || under) memos.set(el, { under });
    else memos.delete(el);
  };
  if (widgets.length) {
    if (widgets.every((widget) => widget.style)) {
      const saved = widgets.map((widget) => widget.style.display);
      let hit = null;
      try {
        widgets.forEach((widget) => { widget.style.display = 'none'; });
        hit = read();
      } finally {
        widgets.forEach((widget, i) => { widget.style.display = saved[i]; });
      }
      remember(hit);
      return hit.offset;
    }
    const memo = memos && memos.get(el);
    if (memo && memo.under === null) return 0;
    if (memo && memo.under && memo.under.isConnected && memo.under.parentNode === el.parentNode) {
      return clamp(marginBottomEdge(memo.under) - read().anchor);
    }
    return stacked;
  }
  const hit = read();
  remember(hit);
  return hit.offset;
};

/**
 * Whether a computed style allows the page to end between the element's children: an ordinary
 * block in normal flow with visible overflow, no columns, and no `break-inside: avoid`.
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @returns {boolean} True when it can be split.
 */
const splittableStyle = (style) => {
  if (!style || outOfFlow(style)) return false;
  if (style.display !== 'block' && style.display !== 'list-item') return false;
  const visible = (v) => !v || v === 'visible' || v === 'clip';
  if (!visible(style.overflow) || !visible(style.overflowX) || !visible(style.overflowY)) return false;
  const columns = style.columnCount;
  if (columns && columns !== 'auto' && columns !== '1') return false;
  const avoid = (v) => v === 'avoid' || v === 'avoid-page';
  return !avoid(style.breakInside) && !avoid(style.pageBreakInside);
};

/**
 * The element that holds a wrapper node's children, when the page may end between them: the
 * node's own DOM when it is its content DOM, or a node view's contentDOM reached through only
 * children that add no box of their own (no margin, padding or border on top or bottom).
 * @param {object} desc - ProseMirror view desc of the node.
 * @returns {HTMLElement|null} Content element, or null to measure the node whole.
 */
export const contentHost = (desc) => {
  if (!desc || !desc.dom || desc.dom.nodeType !== 1 || !desc.contentDOM || desc.contentDOM.nodeType !== 1) return null;
  if (!splittableStyle(styleOf(desc.dom))) return null;
  for (let el = desc.contentDOM; el !== desc.dom; el = el.parentElement) {
    const parent = el.parentElement;
    if (!parent || !desc.dom.contains(parent) || parent.childElementCount !== 1) return null;
    const style = styleOf(el);
    if (!splittableStyle(style) || !collapsesThrough(style, 'top') || !collapsesThrough(style, 'bottom')
      || px(style.marginTop) !== 0 || px(style.marginBottom) !== 0) return null;
  }
  return desc.contentDOM;
};

/**
 * Box of a wrapper for `flattenPageBlocks`: its own margins and its top/bottom insets.
 * @param {HTMLElement} el - Wrapper element.
 * @returns {{marginTop: number, marginBottom: number, insetTop: number, insetBottom: number,
 *   collapseTop: boolean, collapseBottom: boolean}} Box.
 */
const wrapperBox = (el) => {
  const style = styleOf(el);
  const inset = (Side) => px(style && style[`padding${Side}`])
    + (style && style[`border${Side}Style`] && style[`border${Side}Style`] !== 'none' ? px(style[`border${Side}Width`]) : 0);
  return {
    marginTop: px(style && style.marginTop),
    marginBottom: px(style && style.marginBottom),
    insetTop: inset('Top'),
    insetBottom: inset('Bottom'),
    collapseTop: collapsesThrough(style, 'top'),
    collapseBottom: collapsesThrough(style, 'bottom'),
  };
};

/**
 * Inject the widget styles once per document.
 * @param {Document} doc - Document.
 * @returns {void}
 */
const ensureStyles = (doc) => {
  if (!doc || !doc.head || doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    '.q-page-spacer,.q-page-tail,.q-page-head{position:relative;display:block;box-sizing:border-box;margin:0;padding:0;border:0;',
    '-webkit-user-select:none;user-select:none;cursor:text;}',
    // A page end is drawn below every wrapped (floated) image, never across one (see
    // computePageLayout). The head (page 1's header) has no height and nothing above it.
    '.q-page-spacer,.q-page-tail{clear:both;}',
    '.q-page-head{height:0;cursor:default;}',
    // The bands reach the sheet edges (see paintWidget) and take the clicks there, so a click in
    // a side margin beside a page end lands on that page like one in the text column.
    '.q-page-spacer__end,.q-page-spacer__start{position:absolute;left:0;right:0;}',
    '.q-page-spacer__end{top:0;}.q-page-spacer__start{bottom:0;}',
    '.q-page-spacer__gap{position:absolute;background:var(--q-page-canvas,var(--q-canvas,#EEECE7));}',
    // The soft sheet edges (the end of one sheet, the start of the next) only span the sheet
    // width; the band itself bleeds past the sides to hide the sheet shadow.
    '.q-page-spacer__gap::before{content:"";position:absolute;top:0;bottom:0;left:var(--q-page-bleed,6px);',
    'right:var(--q-page-bleed,6px);box-shadow:inset 0 7px 6px -7px rgba(0,0,0,.2),inset 0 -7px 6px -7px rgba(0,0,0,.2);}',
    // After a manual page break the rest of the page says so (the break itself has no height in
    // Pages view, like in print), when there is room for the label.
    '.q-page-spacer[data-forced="true"][data-label="true"] .q-page-spacer__end::before{content:"Page break";',
    'position:absolute;top:10px;left:0;right:0;text-align:center;',
    'font-family:var(--q-font-sans,system-ui,-apple-system,"Segoe UI",sans-serif);font-size:11px;font-weight:500;',
    'letter-spacing:.02em;line-height:1;color:var(--q-page-number,var(--q-faint,#716C65));}',
    // Header and footer bands: three slots (left, centre, right) in the page margin, across the
    // text column. Small, muted, one line with an ellipsis; images at their own height, but never
    // closer than BAND_EDGE_INSET_PX to the paper edge (where printers clip) or BAND_INSET_PX to
    // the text, nor wider than their slot (the exports draw them at the same box: pageSetup.js
    // bandImageBox, and the PDF margin boxes take the same padding).
    '.q-page-band{position:absolute;left:0;right:0;box-sizing:border-box;display:grid;',
    `grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);column-gap:${BAND_COLUMN_GAP_PX}px;align-items:center;`,
    `padding:${BAND_EDGE_INSET_PX}px 0 ${BAND_INSET_PX}px;pointer-events:none;overflow:hidden;`,
    'font-family:var(--q-font-sans,system-ui,-apple-system,"Segoe UI",sans-serif);font-size:11px;',
    'font-weight:400;font-style:normal;text-transform:none;text-decoration:none;text-align:left;',
    'line-height:1.2;letter-spacing:.02em;font-variant-numeric:tabular-nums;color:var(--q-page-number,var(--q-faint,#716C65));}',
    `.q-page-band[data-band="footer"]{padding:${BAND_INSET_PX}px 0 ${BAND_EDGE_INSET_PX}px;}`,
    '.q-page-band[data-empty="true"]{display:none;}',
    '.q-page-band__slot{min-width:0;height:100%;display:flex;align-items:center;overflow:hidden;}',
    '.q-page-band__slot[data-slot="left"]{justify-content:flex-start;}',
    '.q-page-band__slot[data-slot="center"]{justify-content:center;text-align:center;}',
    '.q-page-band__slot[data-slot="right"]{justify-content:flex-end;text-align:right;}',
    // Only the centre slot is used: it takes the whole column (a long title is not cut at a third).
    '.q-page-band[data-span="center"] .q-page-band__slot[data-slot="center"]{grid-column:1 / -1;}',
    '.q-page-band[data-span="center"] .q-page-band__slot:not([data-slot="center"]){display:none;}',
    '.q-page-band__text{min-width:0;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
    // The text is generated content, so it never adds text to the document DOM (a spacer inside
    // a generated section must not change that section's textContent).
    '.q-page-band__text::before{content:attr(data-text);}',
    '.q-page-band__slot img{display:block;width:auto;max-width:100%;max-height:100%;object-fit:contain;}',
    // A spacer inside a wrapper (generated section, blockquote, list) paints the margins it spans
    // with the sheet color across the whole sheet width, so the wrapper's tint, border or review
    // mark stops at the page end like on paper.
    '.q-page-spacer[data-nested="true"] .q-page-spacer__end,.q-page-spacer[data-nested="true"] .q-page-spacer__start{',
    'background:var(--q-page-sheet,transparent);}',
    // The gap cursor on a blank page (see emptyPageCursor) follows the text color, so it stays
    // visible on a dark sheet.
    '.q-paginated .ProseMirror-gapcursor:after{border-top-color:currentColor;}',
    '@media print{.q-page-spacer,.q-page-tail,.q-page-head{display:none!important;}}',
  ].join('');
  doc.head.appendChild(style);
};

/**
 * Page number label. A sheet stretched by a block taller than a page covers several pages and
 * shows their range ("2-4", "Pages 2-4 of 5").
 * @param {number} page - Page (the last page of the sheet).
 * @param {number} total - Pages.
 * @param {string} format - 'n' | 'n-of-m' | 'none'.
 * @param {number} [firstPage] - First page of the sheet (defaults to `page`).
 * @returns {string} Label.
 */
export const formatPageNumber = (page, total, format, firstPage = page) => {
  if (format === 'none') return '';
  const range = Number.isFinite(firstPage) && firstPage < page;
  if (format === 'n-of-m') return range ? `Pages ${firstPage}-${page} of ${total}` : `Page ${page} of ${total}`;
  return range ? `${firstPage}-${page}` : String(page);
};

/** Live-field values a band may show besides the page numbers. */
const FIELD_KEYS = ['title', 'customer', 'project', 'date'];

/**
 * Normalize the live-field context ({title, customer, project, date}: strings).
 * @param {unknown} value - Raw context.
 * @returns {{title: string, customer: string, project: string, date: string}|null} Context.
 */
const normalizeFieldContext = (value) => {
  if (!value || typeof value !== 'object') return null;
  const out = {};
  FIELD_KEYS.forEach((key) => {
    const raw = value[key];
    if (typeof raw === 'string') out[key] = raw;
    else out[key] = typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : '';
  });
  return out;
};

/**
 * The page setup the Pages view draws: the document's (`config.pageSetup`), or without one the
 * defaults with the footer from `pageNumberFormat` ('n' = the centered page number).
 * @param {{pageSetup?: object|null, pageNumberFormat?: string}} config - Settings.
 * @returns {object} Normalized page setup.
 */
export const effectivePageSetup = (config) => {
  if (config && config.pageSetup) return config.pageSetup;
  const setup = normalizePageSetup(null);
  const format = config && config.pageNumberFormat;
  if (format === 'none') setup.footer.center = { type: 'none' };
  else if (format === 'n-of-m') setup.footer.center = { type: 'text', text: PAGE_NUMBER_TEXT.ofTotal };
  return setup;
};

/**
 * Paper of a sheet as it lies: landscape swaps width and height.
 * @param {object} config - Settings ({paperSize, pageSetup}).
 * @returns {{id: string, width: number, height: number}} Paper (px).
 */
export const sheetPaper = (config) => {
  const paper = resolvePaper(config && config.paperSize);
  const landscape = Boolean(config && config.pageSetup && config.pageSetup.orientation === 'landscape');
  if (!landscape) return paper;
  return { id: paper.id, width: Math.max(paper.width, paper.height), height: Math.min(paper.width, paper.height) };
};

/**
 * Page margin of a page setup (px), used when the sheet sets no padding.
 * @param {object} config - Settings.
 * @returns {number} Margin (px).
 */
const presetMargin = (config) => {
  const preset = config && config.pageSetup ? MARGIN_PRESETS[config.pageSetup.margins] : null;
  return preset ? Math.round(preset.in * 96) : DEFAULT_MARGIN;
};

/**
 * Resolve one band for drawing: live fields filled in (plain text, drawn as generated content,
 * never as HTML), image URLs checked against the allowlist again (anything else is dropped).
 * @param {object|null} band - Band ({left, center, right} slots).
 * @param {object} ctx - Field context with `page` and `pages`.
 * @returns {{left: object, center: object, right: object, empty: boolean, span: boolean}} Band to draw.
 */
export const resolveBand = (band, ctx) => {
  const out = { empty: true, span: false };
  SLOT_POSITIONS.forEach((position) => {
    const raw = band && typeof band === 'object' ? band[position] : null;
    let slot = { type: 'none' };
    if (raw && raw.type === 'text') {
      const text = renderSlotText(raw.text, ctx);
      if (text.trim()) slot = { type: 'text', text };
    } else if (raw && raw.type === 'image' && isAllowedImageUrl(raw.src)) {
      const height = Math.min(IMAGE_HEIGHT_MAX, Math.max(IMAGE_HEIGHT_MIN, Math.round(size(raw.height) || IMAGE_HEIGHT_MIN)));
      slot = { type: 'image', src: raw.src, alt: typeof raw.alt === 'string' ? raw.alt : '', height };
    }
    out[position] = slot;
    if (slot.type !== 'none') out.empty = false;
  });
  out.span = out.center.type !== 'none' && out.left.type === 'none' && out.right.type === 'none';
  return out;
};

/**
 * Header and footer of one sheet (pure). {page} is the sheet's page, or its range ("2-4") for a
 * sheet stretched by a block taller than a page; {pages} the page count. The first-page bands
 * apply to the sheet that starts on page 1.
 * @param {object} setup - Normalized page setup.
 * @param {{firstPage: number, lastPage?: number, total: number, fieldContext?: object|null}} sheet - Sheet.
 * @returns {{header: object, footer: object}} Resolved bands (see resolveBand).
 */
export const sheetBands = (setup, { firstPage, lastPage = firstPage, total, fieldContext = null }) => {
  const label = Number.isFinite(lastPage) && lastPage > firstPage ? `${firstPage}-${lastPage}` : String(firstPage);
  const ctx = { ...(fieldContext || {}), page: label, pages: total };
  const { header, footer } = bandsForPage(setup, firstPage);
  return { header: resolveBand(header, ctx), footer: resolveBand(footer, ctx) };
};

/**
 * Normalize pagination settings.
 * @param {object} prev - Current settings.
 * @param {object} patch - Changes.
 * @returns {{enabled: boolean, paperSize: *, pageNumberFormat: string, pageSetup: object|null,
 *   fieldContext: object|null, setupKey: string, geometryKey: string}} Settings.
 */
const normalizeConfig = (prev, patch = {}) => {
  const next = { ...prev };
  if (patch && 'enabled' in patch) next.enabled = Boolean(patch.enabled);
  if (patch && patch.paperSize) next.paperSize = patch.paperSize;
  if (patch && PAGE_NUMBER_FORMATS.includes(patch.pageNumberFormat)) next.pageNumberFormat = patch.pageNumberFormat;
  if (patch && 'pageSetup' in patch) next.pageSetup = patch.pageSetup == null ? null : normalizePageSetup(patch.pageSetup);
  if (patch && 'fieldContext' in patch) next.fieldContext = normalizeFieldContext(patch.fieldContext);
  if (next.pageSetup === undefined) next.pageSetup = null;
  if (next.fieldContext === undefined) next.fieldContext = null;
  next.setupKey = JSON.stringify([next.pageSetup, next.fieldContext]);
  const paper = sheetPaper(next);
  next.geometryKey = `${paper.width}x${paper.height}:${next.pageSetup ? next.pageSetup.margins : ''}`;
  return next;
};

/**
 * Whether two configs ask for the same user-facing settings (ignores the refresh counter).
 * @param {object} a - Config.
 * @param {object} b - Config.
 * @returns {boolean} True when equal.
 */
const sameSettings = (a, b) => a.enabled === b.enabled
  && resolvePaper(a.paperSize).width === resolvePaper(b.paperSize).width
  && resolvePaper(a.paperSize).height === resolvePaper(b.paperSize).height
  && a.pageNumberFormat === b.pageNumberFormat
  && a.setupKey === b.setupKey;

const sameConfig = (a, b) => sameSettings(a, b) && a.refresh === b.refresh;

/**
 * The settings a fresh EditorState re-applies (see restoreCommitted).
 * @param {object} config - Settings.
 * @returns {object} Settings patch.
 */
const settingsOf = (config) => ({
  enabled: config.enabled,
  paperSize: config.paperSize,
  pageNumberFormat: config.pageNumberFormat,
  pageSetup: config.pageSetup,
  fieldContext: config.fieldContext,
});

/* ------------------------------------------------------------------------------------------ */
/* Widgets                                                                                      */
/* ------------------------------------------------------------------------------------------ */

/** Attributes every widget root carries: its kind and its decoration key. */
const WIDGET_ATTR = 'data-q-pagination';
const WIDGET_KEY_ATTR = 'data-q-key';

const DRAG_EVENTS = new Set(['dragstart', 'dragenter', 'dragover', 'dragleave', 'drop', 'dragend']);

/**
 * Create a child element with a class.
 * @param {HTMLElement} parent - Parent.
 * @param {string} className - Class.
 * @returns {HTMLElement} Child.
 */
const child = (parent, className) => {
  const el = parent.ownerDocument.createElement('div');
  el.className = className;
  parent.appendChild(el);
  return el;
};

/**
 * Create a header or footer band (three slots) in a widget.
 * @param {HTMLElement} parent - Widget root.
 * @param {'header'|'footer'} which - Band.
 * @returns {HTMLElement} Band.
 */
const bandElement = (parent, which) => {
  const band = child(parent, `${PAGINATION_CLASSES.band} ${which === 'header' ? PAGINATION_CLASSES.header : PAGINATION_CLASSES.footer}`);
  band.setAttribute('data-band', which);
  band.setAttribute('data-empty', 'true');
  SLOT_POSITIONS.forEach((position) => {
    child(band, PAGINATION_CLASSES.slot).setAttribute('data-slot', position);
  });
  return band;
};

/**
 * Fill a band's slots (each slot is rebuilt only when what it shows changed, so an image is not
 * reloaded on every repaint). Text is generated content (`data-text` shown by ::before): it is
 * never parsed as HTML and adds no text to the document DOM. Images are drawn only from URLs the
 * page setup allowlist accepts, checked again here.
 * @param {HTMLElement} bandEl - Band element.
 * @param {object|null} band - Resolved band (see resolveBand), or null for nothing.
 * @returns {void}
 */
const paintBand = (bandEl, band) => {
  const empty = !band || band.empty;
  bandEl.setAttribute('data-empty', empty ? 'true' : 'false');
  bandEl.setAttribute('data-span', band && band.span ? 'center' : 'none');
  Array.from(bandEl.children).forEach((slotEl) => {
    const position = slotEl.getAttribute('data-slot');
    const slot = (!empty && band[position]) || { type: 'none' };
    const signature = JSON.stringify(slot);
    if (slotEl.qSlotSignature === signature) return;
    slotEl.qSlotSignature = signature;
    while (slotEl.firstChild) slotEl.removeChild(slotEl.firstChild);
    slotEl.setAttribute('data-type', slot.type);
    const doc = slotEl.ownerDocument;
    if (slot.type === 'text') {
      const text = doc.createElement('span');
      text.className = PAGINATION_CLASSES.text;
      text.setAttribute('data-text', String(slot.text));
      slotEl.appendChild(text);
    } else if (slot.type === 'image' && isAllowedImageUrl(slot.src)) {
      const img = doc.createElement('img');
      img.draggable = false;
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      img.alt = String(slot.alt || '');
      img.style.height = `${slot.height}px`;
      // A missing image leaves its slot empty instead of drawing a broken-image icon.
      img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
      img.src = slot.src;
      slotEl.appendChild(img);
    }
  });
};

/**
 * The header or footer band of a widget.
 * @param {HTMLElement} el - Widget root.
 * @param {'header'|'footer'} which - Band.
 * @returns {HTMLElement|null} Band.
 */
const bandOf = (el, which) => {
  const cls = which === 'header' ? PAGINATION_CLASSES.header : PAGINATION_CLASSES.footer;
  return Array.from(el.children).find((node) => node.classList.contains(cls)) || null;
};

/**
 * Write widget geometry into its DOM (skipped when nothing changed). ProseMirror ignores these
 * mutations because they happen inside a widget.
 * @param {HTMLElement} el - Widget root.
 * @param {object} data - Widget data.
 * @returns {void}
 */
const paintWidget = (el, data) => {
  if (!el || !data) return;
  const signature = JSON.stringify(data);
  if (el.qPaginationSignature === signature) return;
  el.qPaginationSignature = signature;
  // Horizontal geometry: `insetLeft/Right` is how far the widget's own box sits from the sheet
  // edges (the page margins at the top level; more inside a wrapper or list).
  const insetLeft = finite(data.insetLeft === undefined ? data.marginLeft : data.insetLeft);
  const insetRight = finite(data.insetRight === undefined ? data.marginRight : data.insetRight);
  const header = bandOf(el, 'header');
  const footer = bandOf(el, 'footer');
  // The bands span the page's text column, wherever the widget sits.
  [header, footer].forEach((band) => {
    if (!band) return;
    band.style.left = `${finite(data.marginLeft) - insetLeft}px`;
    band.style.right = `${finite(data.marginRight) - insetRight}px`;
  });
  if (header) {
    paintBand(header, data.header);
    header.style.height = `${data.marginTop}px`;
  }
  if (footer) {
    paintBand(footer, data.footer);
    footer.style.height = `${data.marginBottom}px`;
    footer.setAttribute('data-page', data.label || String(data.page));
  }
  if (data.kind === 'head') {
    // Page 1's header: in the sheet's top padding, right above the head (which has no height).
    if (header) {
      header.style.top = `${-data.marginTop}px`;
      header.style.bottom = '';
    }
    return;
  }
  el.style.height = `${Math.max(0, data.height)}px`;
  el.setAttribute('data-page', String(data.page));
  el.setAttribute('data-nested', data.nested ? 'true' : 'false');
  if (data.kind === 'tail') {
    if (footer) footer.style.top = '100%';
    // A last page that runs a little over (a final empty paragraph reaching into the bottom
    // margin, see computePageLayout) pulls the tail up, so the sheet still ends on the page.
    const fill = finite(data.fill === undefined ? data.height : data.fill);
    el.style.height = `${Math.max(0, fill)}px`;
    el.style.marginTop = fill < 0 ? `${fill}px` : '0px';
    return;
  }
  const end = el.querySelector(`.${PAGINATION_CLASSES.end}`);
  const gap = el.querySelector(`.${PAGINATION_CLASSES.gap}`);
  const start = el.querySelector(`.${PAGINATION_CLASSES.start}`);
  // The next block's dropped top margin (`shift`) is drawn by the block itself, below the spacer.
  const startHeight = Math.max(0, data.marginTop - (data.shift || 0));
  const endHeight = Math.max(0, data.height - startHeight - data.gap);
  el.setAttribute('data-forced', data.forced ? 'true' : 'false');
  el.setAttribute('data-label', data.forced && data.fill >= MIN_BREAK_LABEL_ROOM ? 'true' : 'false');
  // The end and start bands reach the sheet edges, so clicks in the side margins land on them
  // (nested ones are also sheet colored, see the styles).
  [end, start].forEach((band) => {
    if (!band) return;
    band.style.left = `${-insetLeft}px`;
    band.style.right = `${-insetRight}px`;
  });
  if (end) end.style.height = `${endHeight}px`;
  if (start) start.style.height = `${startHeight}px`;
  if (gap) {
    gap.style.bottom = `${startHeight}px`;
    gap.style.height = `${data.gap}px`;
    gap.style.left = `calc(${-insetLeft}px - var(--q-page-bleed, 6px))`;
    gap.style.right = `calc(${-insetRight}px - var(--q-page-bleed, 6px))`;
  }
  // This page's footer sits in its bottom margin, above the gap; the next page's header fills
  // that page's whole top margin below the gap (part of it may be the next block's own dropped
  // margin, below the spacer).
  if (footer) footer.style.bottom = `${startHeight + data.gap}px`;
  if (header) header.style.bottom = `${startHeight - data.marginTop}px`;
};

/** Most a measured widget height may differ from the math before it is left as computed (px). */
const ALIGN_TOLERANCE = 2;
/** Widget heights snap to the layout grid browsers use (1/64 px), so rounding never adds up. */
const LAYOUT_UNIT = 64;

/**
 * Snap the widget heights of a layout to the real layout, in place (`data.height` of each
 * widget, and the tail's `fill`). The math sums measured heights and computed margins, while the
 * browser lays margins out on a 1/64 px grid, so a page of many paragraphs can end a tenth of a
 * pixel off, which adds up over a long document. The content between two widgets lays out the
 * same whatever their heights (a widget is a block with a height and no margins), so this reads
 * where each widget starts relative to the one before and sizes it so every sheet ends exactly on
 * its page boundary (absolute targets, so nothing accumulates). It only reads, never moves a page
 * end, and stops at the first widget it cannot place or whose correction is over ALIGN_TOLERANCE
 * px (that one and the rest keep the math), so it cannot feed back.
 * @param {Array<{key: string, kind: string, data: object}>} widgets - Spacers in page order, then the tail.
 * @param {Element[]} widgetEls - Widget elements currently in the DOM.
 * @param {{dom: HTMLElement, paperHeight: number, gap: number, marginTop: number, marginBottom: number}} geometry - Sheet geometry.
 * @returns {number} How many widgets were aligned.
 */
export function alignToSheets(widgets, widgetEls, { dom, paperHeight, gap, marginTop, marginBottom }) {
  if (!dom || typeof dom.getBoundingClientRect !== 'function' || !widgetEls || !widgetEls.length) return 0;
  const rects = new Map();
  widgetEls.forEach((el) => {
    const key = el.getAttribute(WIDGET_KEY_ATTR);
    if (!key || rects.has(key)) return;
    const rect = el.getBoundingClientRect();
    rects.set(key, { top: rect.top, bottom: rect.bottom, marginTop: px(el.style.marginTop) });
  });
  const rootRect = dom.getBoundingClientRect();
  if (!rootRect || !(rootRect.height > 0)) return 0;
  const snap = (value) => Math.round(value * LAYOUT_UNIT) / LAYOUT_UNIT;
  // `measuredBottom` is where the previous widget (or the top padding) ends now, `plannedBottom`
  // where it will end with the new heights; the content in between keeps its own height.
  let measuredBottom = rootRect.top + finite(dom.clientTop) + marginTop;
  let plannedBottom = measuredBottom;
  let sheetTop = rootRect.top; // target top of the current sheet
  let aligned = 0;
  for (let i = 0; i < widgets.length; i += 1) {
    const { key, kind, data } = widgets[i];
    const rect = rects.get(key);
    if (!rect || !data) return aligned;
    const pages = Math.max(1, data.page - (data.firstPage || data.page) + 1);
    const sheetBottom = sheetTop + pages * paperHeight + (pages - 1) * gap;
    if (kind === 'tail') {
      // The tail's top without the margin it has now, then the fill up to the bottom margin.
      const top = plannedBottom + (rect.top - rect.marginTop - measuredBottom);
      const fill = snap(sheetBottom - marginBottom - top);
      const model = finite(data.fill === undefined ? data.height : data.fill);
      if (!Number.isFinite(fill) || Math.abs(fill - model) > ALIGN_TOLERANCE) return aligned;
      data.fill = fill;
      data.height = Math.max(0, fill);
      return aligned + 1;
    }
    const top = plannedBottom + (rect.top - measuredBottom);
    const startHeight = Math.max(0, marginTop - (data.shift || 0));
    const height = snap(sheetBottom + gap + startHeight - top);
    if (!(height >= 0) || Math.abs(height - data.height) > ALIGN_TOLERANCE) return aligned;
    data.height = height;
    measuredBottom = rect.bottom;
    plannedBottom = top + height;
    sheetTop = sheetBottom + gap;
    aligned += 1;
  }
  return aligned;
}

/* ------------------------------------------------------------------------------------------ */
/* Controller (plugin view)                                                                     */
/* ------------------------------------------------------------------------------------------ */

const requestFrame = (cb) => (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
  ? { raf: window.requestAnimationFrame(cb) }
  : { timeout: setTimeout(cb, 16) });

const cancelFrame = (handle) => {
  if (!handle) return;
  if (handle.raf !== undefined && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(handle.raf);
  }
  if (handle.timeout !== undefined) clearTimeout(handle.timeout);
};

/**
 * Measures the view and keeps the widget decorations in sync. One per editor view.
 */
class PaginationController {
  /**
   * @param {import('@tiptap/pm/view').EditorView} view - View.
   * @param {object} shared - Per-editor shared state ({ data, storage, options, committed }).
   */
  constructor(view, shared) {
    this.view = view;
    this.shared = shared;
    this.frame = null;
    this.retry = null;
    this.attached = false;
    this.cache = new WeakMap();
    this.wrapCache = new WeakMap();
    // Heights held for blocks pushed below a sheet-leading float (see measure) and where each
    // float sat when no widget was in front of it (see floatOffsetOf).
    this.besideHeights = new WeakMap();
    this.floatMemos = new WeakMap();
    this.observed = new Set();
    this.width = null;
    this.positionSignature = '';
    this.fromDoc = false;
    this.dispatching = false;
    this.aligning = false; // a follow-up frame is aligning freshly drawn widgets
    this.childCount = -1;
    this.onLoad = this.onLoad.bind(this);
    this.onFonts = this.onFonts.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
    const state = paginationPluginKey.getState(view.state);
    if (state && state.config.enabled) this.attach();
    this.restoreCommitted(state);
  }

  /**
   * A restored older EditorState (e.g. `savedState.reconfigure()` when an AI review is
   * finished) brings back the plugin state it had then. If the settings were changed since,
   * re-apply the last committed ones (outside the current update cycle).
   * @param {object|undefined} state - Plugin state of the view's state.
   * @returns {void}
   */
  restoreCommitted(state) {
    const { committed } = this.shared;
    if (!state || !committed || sameSettings(state.config, committed)) return;
    const settings = settingsOf(committed);
    Promise.resolve().then(() => {
      const { view } = this;
      if (!view || view.isDestroyed || this.destroyed) return;
      const current = paginationPluginKey.getState(view.state);
      if (!current || sameSettings(current.config, committed)) return;
      view.dispatch(view.state.tr.setMeta(paginationPluginKey, { config: settings }).setMeta('addToHistory', false));
    });
  }

  /** @returns {object} Current plugin state. */
  get pluginState() {
    return paginationPluginKey.getState(this.view.state);
  }

  /**
   * Plugin view update.
   * @param {import('@tiptap/pm/view').EditorView} view - View.
   * @param {import('@tiptap/pm/state').EditorState} prevState - Previous state.
   * @returns {void}
   */
  update(view, prevState) {
    this.view = view;
    const next = this.pluginState;
    const prev = paginationPluginKey.getState(prevState);
    if (!next) return;
    if (!prev || !sameConfig(prev.config, next.config)) {
      this.syncStorage(next.config);
      // Only what the header and footer show changed (fields, page number format): the page
      // geometry is the same, so the cached measurements stay and the widgets repaint in place.
      const bandsOnly = prev && prev.config.enabled && next.config.enabled && this.attached
        && prev.config.geometryKey === next.config.geometryKey && prev.config.refresh === next.config.refresh;
      if (bandsOnly) {
        this.schedule();
      } else if (next.config.enabled) {
        this.attach();
        this.invalidateAll();
        this.schedule();
      } else {
        this.detach();
      }
      return;
    }
    if (!next.config.enabled || this.dispatching) return;
    if (view.state.doc !== prevState.doc) this.schedule({ fromDoc: true });
    // Another plugin added or removed an in-flow widget between blocks (no doc change).
    else if (view.dom.childElementCount !== this.childCount) this.schedule();
  }

  /**
   * Mirror the settings into editor.storage.pagination.
   * @param {object} config - Settings.
   * @returns {void}
   */
  syncStorage(config) {
    this.shared.committed = config;
    const { storage } = this.shared;
    if (!storage) return;
    storage.enabled = config.enabled;
    storage.paperSize = resolvePaper(config.paperSize).id;
    storage.orientation = config.pageSetup ? config.pageSetup.orientation : 'portrait';
    storage.margins = config.pageSetup ? config.pageSetup.margins : 'normal';
    if (!config.enabled) storage.pageCount = 0;
  }

  /** Start observing. @returns {void} */
  attach() {
    if (this.attached) return;
    this.attached = true;
    const { dom } = this.view;
    const doc = dom.ownerDocument;
    ensureStyles(doc);
    this.syncStorage(this.pluginState.config);
    dom.addEventListener('load', this.onLoad, true);
    const win = doc && doc.defaultView;
    if (win && typeof win.ResizeObserver === 'function') {
      this.resizeObserver = new win.ResizeObserver((entries) => this.onResize(entries));
      this.resizeObserver.observe(dom);
    } else if (win) {
      win.addEventListener('resize', this.onWindowResize);
    }
    const fonts = doc && doc.fonts;
    if (fonts) {
      if (fonts.ready && typeof fonts.ready.then === 'function') fonts.ready.then(this.onFonts, () => {});
      if (typeof fonts.addEventListener === 'function') fonts.addEventListener('loadingdone', this.onFonts);
    }
    this.schedule();
  }

  /** Stop observing and forget measurements. @returns {void} */
  detach() {
    if (!this.attached) return;
    this.attached = false;
    const { dom } = this.view;
    const doc = dom.ownerDocument;
    dom.removeEventListener('load', this.onLoad, true);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = null;
    if (doc && doc.defaultView) doc.defaultView.removeEventListener('resize', this.onWindowResize);
    if (doc && doc.fonts && typeof doc.fonts.removeEventListener === 'function') {
      doc.fonts.removeEventListener('loadingdone', this.onFonts);
    }
    cancelFrame(this.frame);
    this.frame = null;
    clearTimeout(this.retry);
    this.retry = null;
    this.observed.clear();
    this.cache = new WeakMap();
    this.wrapCache = new WeakMap();
    this.besideHeights = new WeakMap();
    this.floatMemos = new WeakMap();
    this.width = null;
    this.positionSignature = '';
    this.childCount = -1;
    this.shared.data.clear();
  }

  /** Forget every cached block measurement. @returns {void} */
  invalidateAll() {
    this.cache = new WeakMap();
    this.wrapCache = new WeakMap();
    this.besideHeights = new WeakMap();
    this.floatMemos = new WeakMap();
  }

  /**
   * ResizeObserver callback.
   * @param {ResizeObserverEntry[]} entries - Entries.
   * @returns {void}
   */
  onResize(entries) {
    let changed = false;
    entries.forEach((entry) => {
      if (entry.target === this.view.dom) {
        // Same measure as measure() stores (clientWidth includes the page margins), so a
        // height-only change (a page added while typing) keeps every cached block measurement.
        const width = entry.target.clientWidth;
        if (width !== this.width) {
          this.invalidateAll();
          changed = true;
        }
      } else {
        this.cache.delete(entry.target);
        changed = true;
      }
    });
    if (changed) this.schedule();
  }

  /** Window resize fallback (no ResizeObserver). @returns {void} */
  onWindowResize() {
    this.invalidateAll();
    this.schedule();
  }

  /**
   * Capture-phase `load` (images) inside the editor.
   * @param {Event} event - Event.
   * @returns {void}
   */
  onLoad(event) {
    // Forget the measured block holding the image (and any wrapper around it).
    const root = this.view.dom;
    let current = event.target;
    let inside = false;
    while (current && current !== root) {
      if (current.nodeType === 1) {
        this.cache.delete(current);
        this.wrapCache.delete(current);
      }
      current = current.parentNode;
      if (current === root) inside = true;
    }
    if (!inside) this.invalidateAll();
    this.schedule();
  }

  /** Fonts finished loading. @returns {void} */
  onFonts() {
    if (!this.attached) return;
    this.invalidateAll();
    this.schedule();
  }

  /**
   * The direct child of `container` that contains `node`.
   * @param {Node} node - Node.
   * @param {Element} [container] - Container (the editor element by default).
   * @returns {Element|null} Child element.
   */
  childAncestor(node, container = this.view.dom) {
    let current = node;
    while (current && current.parentNode && current.parentNode !== container) current = current.parentNode;
    return current && current.parentNode === container ? current : null;
  }

  /**
   * Measure the children of one container (the editor element, or the content element of a
   * wrapper or list) into block tree nodes for `flattenPageBlocks`. One in-order walk over the
   * container's DOM children (view.nodeDOM scans from the first child each time, which made every
   * frame quadratic on long documents); nodeDOM only as a fallback. Measurements are cached per
   * element and PM node, so unchanged blocks are not read again.
   * @param {HTMLElement} container - Element holding the children's DOM.
   * @param {object} containerDesc - View desc owning `container` (view.docView at the top).
   * @param {import('@tiptap/pm/model').Node} parent - Node whose children these are.
   * @param {number} contentStart - Document position of `parent`'s content start.
   * @param {number} depth - Wrapper nesting depth (0 at the top level).
   * @param {boolean} items - The children are list items: each one is a unit, never descended.
   * @param {{leafEls: Element[], widgetEls: Element[], held: Element[], remembered: Element[]}} out - Collects
   *   the measured unit elements (to observe), the pagination widgets found on the way (to
   *   repaint), and the blocks with a held height (`remembered`; `held` when it was used).
   * @returns {Array<object>} Tree nodes in order.
   */
  collect(container, containerDesc, parent, contentStart, depth, items, out) {
    const { view } = this;
    const { options } = this.shared;
    const breakName = options.breakNodeName;
    const transparent = options.transparentNodes || PAGINATION_TRANSPARENT_NODES;
    const lists = options.listNodes || PAGINATION_LIST_NODES;
    const children = Array.from(container.children);
    const nested = container !== view.dom;
    const entries = [];
    let cursor = 0;
    parent.forEach((node, offset) => {
      const pos = contentStart + offset;
      let el = null;
      for (let i = cursor; i < children.length; i += 1) {
        const desc = children[i].pmViewDesc;
        if (!desc || desc.parent !== containerDesc || !desc.node) continue; // widget or foreign element
        if (desc.node === node) {
          el = children[i];
          cursor = i + 1;
        }
        break;
      }
      if (!el) {
        try {
          el = view.nodeDOM(pos);
        } catch {
          el = null;
        }
        if (el && el.parentNode !== container) el = this.childAncestor(el, container);
        if (el) {
          const at = children.indexOf(el, cursor);
          if (at >= 0) cursor = at + 1;
        }
      }
      entries.push({ node, pos, el });
    });

    // In-flow elements other plugins put between blocks count toward the next block; the
    // pagination widgets are only noted (they are what this engine sizes).
    const extra = new Array(entries.length).fill(0);
    const indexOf = new Map();
    entries.forEach((entry, i) => entry.el && indexOf.set(entry.el, i));
    let nextIndex = 0;
    children.forEach((el) => {
      if (indexOf.has(el)) {
        nextIndex = indexOf.get(el) + 1;
        return;
      }
      if (el.hasAttribute(WIDGET_ATTR)) {
        out.widgetEls.push(el);
        return;
      }
      if (el.classList.contains(PAGINATION_CLASSES.spacer) || el.classList.contains(PAGINATION_CLASSES.tail)) return;
      const style = styleOf(el);
      if (outOfFlow(style)) return;
      const box = measureBlock(el);
      const target = Math.min(nextIndex, entries.length - 1);
      if (target >= 0) extra[target] += box.height + Math.max(0, box.marginTop) + Math.max(0, box.marginBottom);
    });

    let stacked = 0; // margin box heights of the floats right before the current entry
    return entries.map((entry, i) => {
      const { node, pos, el } = entry;
      const at = { pos, container: nested ? container : null };
      const name = node.type.name;
      const isList = lists.includes(name);
      if (!items && el && depth < MAX_NEST_DEPTH && (isList || transparent.includes(name))) {
        const desc = el.pmViewDesc;
        let wrap = this.wrapCache.get(el);
        if (!wrap || wrap.node !== node || (wrap.host && !el.contains(wrap.host))) {
          const host = desc && desc.node === node ? contentHost(desc) : null;
          wrap = { node, host, box: host ? wrapperBox(el) : null };
          this.wrapCache.set(el, wrap);
        }
        if (wrap.host) {
          const kids = this.collect(wrap.host, desc, node, pos + 1, depth + 1, isList, out);
          stacked = 0;
          if (kids.length) return { ...wrap.box, children: kids, at, extraBefore: extra[i] };
        }
      }
      let box = el ? this.cache.get(el) : null;
      if (!box || box.node !== node) {
        box = { node, ...measureFlowBlock(el) };
        if (el) this.cache.set(el, box);
      }
      if (el) out.leafEls.push(el);
      // A float's offset depends on the floats before it, so it is read on every measurement
      // (wrapped images are few); see floatOffsetOf.
      let floatOffset = 0;
      if (box.floating) {
        floatOffset = el ? floatOffsetOf(el, container, stacked, this.floatMemos) : stacked;
        stacked += box.height;
      } else {
        stacked = 0;
      }
      // A block the page ended before because it did not fit beside a float group that starts
      // the sheet keeps the height it had beside the floats (see measure).
      let height = box.height;
      const held = el && !box.floating ? this.besideHeights.get(el) : null;
      if (held) {
        out.remembered.push(el);
        if (held.node !== node && node.content.size < held.size) {
          // Text was removed: it may fit beside the floats now.
          this.besideHeights.delete(el);
        } else if (held.height > height) {
          height = held.height;
          out.held.push(el);
        }
      }
      return {
        el,
        height,
        marginTop: box.marginTop,
        marginBottom: box.marginBottom,
        floating: Boolean(box.floating),
        floatOffset,
        floatBottom: box.floatBottom || 0,
        avoidsFloats: Boolean(box.avoidsFloats),
        extraBefore: extra[i],
        isBreak: name === breakName,
        // Print keeps a heading with the block after it (page-break-after: avoid), Word too.
        keepWithNext: name === 'heading',
        // The empty paragraph the editor keeps at the end of the document (see computePageLayout).
        trailingEmpty: !nested && i === entries.length - 1 && i > 0 && node.isTextblock && node.content.size === 0,
        at,
      };
    });
  }

  /**
   * Request a measurement on the next animation frame (coalesced).
   * @param {{fromDoc?: boolean}} [opts] - Whether a doc change caused it.
   * @returns {void}
   */
  schedule(opts = {}) {
    if (opts.fromDoc) this.fromDoc = true;
    if (this.frame || !this.attached) return;
    this.frame = requestFrame(() => {
      this.frame = null;
      this.measure();
    });
  }

  /** Measure, lay out, and update widgets. @returns {void} */
  measure() {
    const { view } = this;
    if (!this.attached || view.isDestroyed) return;
    const pluginState = this.pluginState;
    if (!pluginState || !pluginState.config.enabled) return;
    if (view.composing) {
      clearTimeout(this.retry);
      this.retry = setTimeout(() => this.schedule(), COMPOSITION_RETRY_MS);
      return;
    }
    const { dom } = view;
    if (!dom.isConnected) return;
    const fromDoc = this.fromDoc;
    this.fromDoc = false;

    const rootStyle = styleOf(dom);
    const width = dom.clientWidth;
    if (width !== this.width) {
      this.invalidateAll();
      this.width = width;
    }
    const { config } = pluginState;
    // Landscape swaps the paper; the margins are the sheet's padding (the host sets it from the
    // page setup), or the page setup's margins when the sheet has none.
    const paper = sheetPaper(config);
    const fallbackMargin = presetMargin(config);
    const hasPadding = rootStyle && (px(rootStyle.paddingTop) || px(rootStyle.paddingBottom));
    const marginTop = hasPadding ? px(rootStyle.paddingTop) : fallbackMargin;
    const marginBottom = hasPadding ? px(rootStyle.paddingBottom) : fallbackMargin;
    const marginLeft = rootStyle ? px(rootStyle.paddingLeft) : fallbackMargin;
    const marginRight = rootStyle ? px(rootStyle.paddingRight) : fallbackMargin;
    const cssGap = rootStyle && typeof rootStyle.getPropertyValue === 'function'
      ? parseFloat(rootStyle.getPropertyValue('--q-page-gap')) : NaN;
    const gap = Number.isFinite(cssGap) && cssGap >= 0 ? cssGap : size(this.shared.options.gap);
    const pageContentHeight = Math.max(MIN_CONTENT_HEIGHT, paper.height - marginTop - marginBottom);

    // The block tree (top-level blocks, and the children of the wrappers and lists the page may
    // end inside), flattened into the units the layout places.
    const { doc } = view.state;
    const run = () => {
      const collected = { leafEls: [], widgetEls: [], held: [], remembered: [] };
      const tree = this.collect(dom, view.docView, doc, 0, 0, false, collected);
      const flat = flattenPageBlocks(tree);
      const result = computePageLayout({
        blocks: flat,
        pageContentHeight,
        chromeHeight: marginBottom + gap + marginTop,
        pageMarginTop: marginTop,
        // A final empty paragraph may reach this far into the bottom margin (clear of the footer).
        trailingAllowance: marginBottom / 2,
      });
      return { collected, units: flat, layout: result };
    };
    // A block that does not fit beside a float group starting its sheet goes below the floats
    // on the next page, where it is wider (the spacer clears the floats) and so shorter: measured
    // there it would fit beside them again, and the page end would flip on every frame. Its
    // height beside the floats is held while the page still ends before it for that reason (it
    // is dropped when the layout no longer does, when text is removed from it, or on a width
    // change).
    let pass = run();
    const heldNow = new Set();
    pass.layout.spacers.forEach((spacer) => {
      const unit = spacer.besideFloat ? pass.units[spacer.beforeIndex] : null;
      if (unit && unit.el) heldNow.add(unit.el);
    });
    pass.collected.remembered.forEach((el) => {
      if (!heldNow.has(el)) this.besideHeights.delete(el);
    });
    const stale = pass.collected.held.filter((el) => !heldNow.has(el));
    heldNow.forEach((el) => {
      const measured = this.cache.get(el);
      if (!measured || !measured.node) return;
      const previous = this.besideHeights.get(el);
      this.besideHeights.set(el, {
        node: measured.node,
        size: measured.node.content.size,
        height: Math.max(size(measured.height), previous ? previous.height : 0),
      });
    });
    // Held heights the layout no longer needs must not shape this layout either.
    if (stale.length) pass = run();
    const { collected, units, layout } = pass;
    this.observeBlocks(collected.leafEls);
    const total = layout.pageCount;
    const common = { total, marginTop, marginBottom, marginLeft, marginRight, gap };
    // Header and footer of every sheet: [first page, last page] (several for a stretched sheet).
    const setup = effectivePageSetup(config);
    const sheets = layout.spacers.map((spacer) => [spacer.pageNumberStarted, spacer.pageNumberEnded])
      .concat([[layout.lastSheetFirstPage, total]]);
    const bands = sheets.map(([firstPage, lastPage]) => sheetBands(setup, {
      firstPage, lastPage, total, fieldContext: config.fieldContext,
    }));
    const labelOf = ([firstPage, lastPage]) => (lastPage > firstPage ? `${firstPage}-${lastPage}` : String(lastPage));
    const atOf = (index) => (index < units.length && units[index].at ? units[index].at : { pos: doc.content.size, container: null });
    // How far a widget inside a wrapper sits from the sheet edges: the content box of the element
    // it is rendered in (read once per element, before any widget is repainted).
    let rootRect = null;
    const insets = new Map();
    const insetsOf = (container) => {
      if (!container) return { insetLeft: marginLeft, insetRight: marginRight };
      if (insets.has(container)) return insets.get(container);
      if (!rootRect) rootRect = dom.getBoundingClientRect();
      const rect = container.getBoundingClientRect();
      const cs = styleOf(container);
      const side = (Side) => px(cs && cs[`padding${Side}`]) + px(cs && cs[`border${Side}Width`]);
      const value = {
        insetLeft: rect.left + side('Left') - rootRect.left,
        insetRight: rootRect.right - (rect.right - side('Right')),
      };
      insets.set(container, value);
      return value;
    };
    const widgets = layout.spacers.map((spacer, i) => {
      const at = atOf(spacer.beforeIndex);
      return {
        key: `q-page-end-${spacer.pageNumberEnded}`,
        kind: 'spacer',
        pos: at.pos,
        data: {
          ...common,
          ...insetsOf(at.container),
          nested: Boolean(at.container),
          kind: 'spacer',
          height: spacer.height,
          fill: spacer.fill,
          shift: spacer.shift,
          forced: spacer.forced,
          page: spacer.pageNumberEnded,
          firstPage: spacer.pageNumberStarted,
          label: labelOf(sheets[i]),
          // This sheet's footer, and the next sheet's header.
          footer: bands[i].footer,
          header: bands[i + 1].header,
        },
      };
    });
    widgets.push({
      key: 'q-page-tail',
      kind: 'tail',
      pos: doc.content.size,
      data: {
        ...common,
        kind: 'tail',
        height: Math.max(0, layout.lastPageFill),
        fill: layout.lastPageFill,
        page: total,
        firstPage: layout.lastSheetFirstPage,
        label: labelOf(sheets[sheets.length - 1]),
        footer: bands[bands.length - 1].footer,
      },
    });
    // Page 1's header sits above the first block (the head widget is only drawn when it shows
    // something, so documents without a header keep exactly the widgets they had).
    const all = bands[0].header.empty ? widgets : [{
      key: 'q-page-head',
      kind: 'head',
      pos: 0,
      data: { ...common, kind: 'head', page: 1, header: bands[0].header },
    }].concat(widgets);

    const positionSignature = all.map((w) => `${w.key}@${w.pos}`).sort().join('|');
    // Compare with where the current widgets actually sit (they are mapped through every doc
    // change), not with the last dispatched positions: typing shifts every later position, and
    // re-dispatching the same layout on each keystroke would add a transaction per key.
    const placedSignature = pluginState.decorations
      .find()
      .map((deco) => `${deco.spec.key}@${deco.from}`)
      .sort()
      .join('|');
    const placedMatches = positionSignature === placedSignature;
    // The widgets in the DOM are the ones this layout keeps: snap their heights to the real
    // layout (sub-pixel margins the math cannot know), so sheets never drift over many pages.
    if (placedMatches) {
      alignToSheets(widgets, collected.widgetEls, { dom, paperHeight: paper.height, gap, marginTop, marginBottom });
    }

    // Fresh data first: toDOM (new widgets) and the in-place repaint both read it. The live
    // widgets are found in the DOM, not remembered from toDOM: ProseMirror reuses widget DOM by
    // key (also across a plugin reconfigure, which recreates this controller), so toDOM is not
    // called again for them.
    const { data, storage } = this.shared;
    data.clear();
    all.forEach((w) => data.set(w.key, w.data));
    collected.widgetEls.forEach((el) => {
      const key = el.getAttribute(WIDGET_KEY_ATTR);
      if (key && data.has(key)) paintWidget(el, data.get(key));
    });

    const countChanged = storage && storage.pageCount !== total;
    if (storage) storage.pageCount = total;
    if (placedMatches && !countChanged) {
      this.positionSignature = positionSignature;
      this.childCount = dom.childElementCount;
      this.aligning = false;
      return;
    }
    this.positionSignature = positionSignature;

    const tr = view.state.tr
      .setMeta(paginationPluginKey, { layout: all.map(({ key, kind, pos }) => ({ key, kind, pos })) })
      .setMeta('addToHistory', false);
    // A block that moved to the next page while typing takes the caret with it: keep it visible.
    if (fromDoc && view.hasFocus()) tr.scrollIntoView();
    this.dispatching = true;
    try {
      view.dispatch(tr);
    } finally {
      this.dispatching = false;
    }
    this.childCount = dom.childElementCount;
    // New widgets are drawn from the math; one more frame aligns them to the real layout (once:
    // that frame keeps the same page ends, so it repaints in place and dispatches nothing).
    if (!placedMatches && !this.aligning) {
      this.aligning = true;
      this.schedule();
    }
  }

  /**
   * Keep the ResizeObserver on exactly the currently measured blocks (top-level blocks and the
   * children of split wrappers; never a wrapper, whose height includes the spacers in it).
   * @param {Element[]} elements - Measured block elements.
   * @returns {void}
   */
  observeBlocks(elements) {
    const ro = this.resizeObserver;
    if (!ro) return;
    const current = new Set();
    elements.forEach((el) => {
      if (!el || el.nodeType !== 1) return;
      current.add(el);
      if (!this.observed.has(el)) ro.observe(el);
    });
    this.observed.forEach((el) => {
      if (!current.has(el)) ro.unobserve(el);
    });
    this.observed = current;
  }

  /** Plugin view destroy. @returns {void} */
  destroy() {
    this.destroyed = true;
    this.detach();
  }
}

/* ------------------------------------------------------------------------------------------ */
/* Extension                                                                                    */
/* ------------------------------------------------------------------------------------------ */

/**
 * Caret for a click on a page that holds no text: the page after a page break that is followed
 * straight away by another break (or by the end of the document). Such a page has no position
 * inside a textblock, so a text caret would land on a neighboring sheet; a gap cursor sits on the
 * clicked page instead (typing there inserts a paragraph, the Gapcursor extension's behavior).
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {number} pos - Widget position (between two top-level blocks).
 * @param {-1|0|1} dir - Which page was clicked: 1 the page that starts at `pos` (a spacer's lower
 *   part), -1 the page that ends at `pos` (a spacer's upper part), 0 the last page (the tail).
 * @param {string} breakName - Page break node name.
 * @returns {GapCursor|null} Gap cursor on that page, or null when the page has text (or none fits).
 */
export const emptyPageCursor = (doc, pos, dir, breakName) => {
  if (pos < 0 || pos > doc.content.size) return null;
  const $pos = doc.resolve(pos);
  if ($pos.depth !== 0) return null;
  const isBreak = (node) => Boolean(node && node.type.name === breakName);
  let at = null;
  if (dir > 0 || dir === 0) {
    // The page starting here (or the last page) is empty when a break or the end comes next.
    const startsEmpty = dir > 0 ? (!$pos.nodeAfter || isBreak($pos.nodeAfter)) : isBreak($pos.nodeBefore);
    if (startsEmpty) at = pos;
  } else if (isBreak($pos.nodeBefore)) {
    // The page ending here ends with a break; it is empty when nothing but a page end precedes
    // that break (GapCursor.valid rejects a text block before it).
    at = pos - $pos.nodeBefore.nodeSize;
  }
  if (at === null) return null;
  const $at = doc.resolve(at);
  return GapCursor.valid($at) ? new GapCursor($at) : null;
};

/**
 * Build the widget decorations for a layout.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {Array<{key: string, kind: string, pos: number}>} widgets - Widgets.
 * @param {(key: string, kind: string) => Function} toDOMFor - Widget DOM factory.
 * @returns {DecorationSet} Decorations.
 */
const buildDecorations = (doc, widgets, toDOMFor) => {
  const max = doc.content.size;
  const decos = widgets
    .filter((w) => w.pos >= 0 && w.pos <= max)
    .map((w) => Decoration.widget(w.pos, toDOMFor(w.key, w.kind), {
      key: w.key,
      side: w.kind === 'tail' ? 1 : -1,
      ignoreSelection: true,
      stopEvent: (event) => !DRAG_EVENTS.has(event.type),
      marks: [],
    }));
  return DecorationSet.create(doc, decos);
};

const Pagination = Extension.create({
  name: 'pagination',

  addOptions() {
    return {
      enabled: false,
      paperSize: 'letter',
      pageNumberFormat: 'n',
      pageSetup: null,
      fieldContext: null,
      gap: 24,
      breakNodeName: 'pageBreak',
      transparentNodes: PAGINATION_TRANSPARENT_NODES,
      listNodes: PAGINATION_LIST_NODES,
    };
  },

  addStorage() {
    return { enabled: false, paperSize: 'letter', orientation: 'portrait', margins: 'normal', pageCount: 0 };
  },

  addCommands() {
    return {
      setPagination: (options = {}) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(paginationPluginKey, { config: options || {} });
          tr.setMeta('addToHistory', false);
        }
        return true;
      },
      refreshPagination: () => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(paginationPluginKey, { refresh: true });
          tr.setMeta('addToHistory', false);
        }
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const { options, storage } = this;
    /** key -> latest widget data from the last measurement. */
    const data = new Map();
    /**
     * `committed` holds the last settings the view applied. A fresh EditorState (content load,
     * review entered or reopened) starts from it instead of the options, so the Pages view
     * survives `view.updateState()`.
     */
    const shared = { data, storage, options, committed: null };

    const toDOMFor = (key, kind) => (view, getPos) => {
      const doc = view.dom.ownerDocument;
      const el = doc.createElement('div');
      el.className = PAGINATION_CLASSES[kind === 'tail' || kind === 'head' ? kind : 'spacer'];
      el.setAttribute('contenteditable', 'false');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute(WIDGET_ATTR, kind);
      el.setAttribute(WIDGET_KEY_ATTR, key);
      el.draggable = false;
      el.style.margin = '0';
      el.style.padding = '0';
      el.style.border = '0';
      if (kind === 'spacer') {
        child(el, PAGINATION_CLASSES.end);
        child(el, PAGINATION_CLASSES.gap);
        child(el, PAGINATION_CLASSES.start);
      }
      // This sheet's footer (spacer, tail) and the next sheet's header (spacer; head: page 1).
      if (kind !== 'head') bandElement(el, 'footer');
      if (kind !== 'tail') bandElement(el, 'header');
      el.addEventListener('mousedown', (event) => {
        if (kind === 'head') {
          event.preventDefault();
          return;
        }
        if (event.button !== 0) return;
        event.preventDefault();
        if (!view.editable || view.isDestroyed) return;
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (typeof pos !== 'number') return;
        const info = data.get(key);
        const rect = el.getBoundingClientRect();
        const nextPageFrom = rect.bottom - (info ? info.marginTop + info.gap : 0);
        const dir = kind === 'tail' || event.clientY < nextPageFrom ? -1 : 1;
        try {
          // A page with no text (between two page breaks, or after a final one) gets a gap
          // cursor on that page; typing there adds a paragraph to it, like Word's paragraph mark.
          // Otherwise prefer a caret in text (end of this page, start of the next): a page break
          // or other atom next to the widget must not end up node-selected, where typing would
          // replace it.
          const $pos = view.state.doc.resolve(pos);
          const selection = emptyPageCursor(view.state.doc, pos, kind === 'tail' ? 0 : dir, options.breakNodeName)
            || Selection.findFrom($pos, dir, true)
            || Selection.findFrom($pos, -dir, true)
            || Selection.near($pos, dir);
          view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
        } catch {
          /* position no longer valid */
        }
        view.focus();
      });
      paintWidget(el, data.get(key));
      return el;
    };

    const initialConfig = normalizeConfig(
      { enabled: false, paperSize: 'letter', pageNumberFormat: 'n', refresh: 0 },
      {
        enabled: options.enabled,
        paperSize: options.paperSize,
        pageNumberFormat: options.pageNumberFormat,
        pageSetup: options.pageSetup || null,
        fieldContext: options.fieldContext || null,
      },
    );

    return [
      new Plugin({
        key: paginationPluginKey,
        state: {
          init: () => ({
            config: shared.committed ? normalizeConfig(initialConfig, shared.committed) : initialConfig,
            decorations: DecorationSet.empty,
          }),
          apply: (tr, prev) => {
            const meta = tr.getMeta(paginationPluginKey);
            let { config, decorations } = prev;
            if (meta && meta.config) config = normalizeConfig(config, meta.config);
            if (meta && meta.refresh) config = { ...config, refresh: (config.refresh || 0) + 1 };
            if (!config.enabled) return { config, decorations: DecorationSet.empty };
            if (meta && meta.layout) {
              decorations = buildDecorations(tr.doc, meta.layout, toDOMFor);
            } else if (tr.docChanged) {
              decorations = decorations.map(tr.mapping, tr.doc);
            }
            return config === prev.config && decorations === prev.decorations ? prev : { config, decorations };
          },
        },
        props: {
          decorations: (state) => paginationPluginKey.getState(state).decorations,
          attributes: (state) => {
            const { config } = paginationPluginKey.getState(state);
            if (!config.enabled) return {};
            return {
              class: PAGINATION_CLASSES.root,
              'data-paper-size': resolvePaper(config.paperSize).id,
              'data-orientation': config.pageSetup ? config.pageSetup.orientation : 'portrait',
            };
          },
        },
        view: (view) => {
          const controller = new PaginationController(view, shared);
          return {
            update: (v, prevState) => controller.update(v, prevState),
            destroy: () => controller.destroy(),
          };
        },
      }),
    ];
  },
});

export default Pagination;
